import { h, Component, createRef, Fragment } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer, withText } from 'preact-i18n';
import Markdown from 'preact-markdown';
import { QRCodeSVG } from 'qrcode.react';
import md5 from 'crypto-js/md5'
import { route } from 'preact-router';

import localUtil from '../../lib/util';
import { getPositionAtTime, getPointsUpToTime, calculateRaceTimeBounds, calculateBearing, calculateDistanceAtTime, findPointIndexBefore } from '../../lib/replayUtils';
import { calculateElevationStats, calculateElevationProfile, downsampleProfile } from '../../lib/elevationUtils';
import ReplayControls from './replayControls';
import ElevationProfile from './elevationProfile';
import CoverageOverlay from './coverageOverlay';
import NewsTickerOverlay from '../news/tickerOverlay';
import StreamingTrackingOverlay from '../streaming/trackingOverlay';

import { firestoreDb } from '../../firebase';
import { doc, onSnapshot } from 'firebase/firestore';

// import * as leaflet from 'leaflet';
import { MapContainer, TileLayer, Marker, Popup, Polyline, Polygon, useMap, useMapEvents, GeoJSON, ScaleControl, Circle, CircleMarker } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from 'leaflet';

import markerSvg from '../../assets/mapicons/marker.svg';
import markerPerson from '../../assets/mapicons/person-duotone.svg';
import markerTentSvg from '../../assets/mapicons/camping-tent.svg';
import markerCircleSvg from '../../assets/mapicons/circle-marker.svg';
import markerDogSvg from '../../assets/mapicons/dog-duotone.svg';
import markerSnowMobileSvg from '../../assets/mapicons/person-snowmobiling-duotone.svg';
import markerPin from '../../assets/mapicons/pinmarker.png';

// Create a replay marker icon with full opacity
// Always position label to the right to avoid flickering during replay
function createReplayMarkerIcon(tracker, idx) {
    const contestant = tracker.contestant || { firstname: 'trackerId', lastname: tracker.trackerId, bib: idx };
    const bearing = tracker.bearing || 0;
    const bgColor = tracker.bgColor || tracker.lineColor || '#ff0000';

    const iconSize = [150, 30];
    const iconAnchor = [3, 15];
    const lineHeight = '1.1em';
    const iconLabelWidth = 120;

    const html = `
        <div
            class='d-flex flex-row justify-content-start align-items-center w-100'
            style='opacity: 1;'
        >
            <div
                class='d-flex align-items-center justify-content-center overflow-hidden rounded-circle me-1'
                style='width: 15px !important; min-width: 15px; height: 15px; background-color: ${bgColor}; border: 2px solid #ffffff; font-size: 7px;'
            ></div>
            <div class='bg-white rounded-pill px-2 border' style='font-size: 11px; color: #333;'>
                <div class='d-flex flex-row align-items-center'>
                    ${bearing ? `<small class='me-1' style='line-height: ${lineHeight}; color: #333;'>
                        <i class='fa-duotone fa-arrow-up' style='transform: rotate(${bearing}deg);'></i>
                    </small>` : ''}
                    <div class='d-flex flex-row text-truncate align-items-center overflow-hidden' style='max-width: ${iconLabelWidth}px;'>
                        ${contestant.country ? localUtil.getFlagSvgString(contestant.country, 15, true) || '' : ''}
                        <span class='mx-1' style='color: #555;'>${contestant.bib}</span>
                        <span style='color: #333;'>${contestant.lastname || ''}</span>
                    </div>
                </div>
            </div>
        </div>
    `;

    return L.divIcon({
        className: '', // Remove default leaflet-div-icon styling
        popupAnchor: [0, -15],
        iconAnchor,
        iconSize,
        html,
    });
}

const iconMarker = new L.Icon({
    iconUrl: markerSvg,
    iconRetinaUrl: markerSvg,
    // popupAnchor:  [45, 45],
    iconAnchor: [15,30],
    iconSize: [30,30],
});
const iconMarkerPerson = new L.Icon({
    iconUrl: markerPerson,
    iconRetinaUrl: markerPerson,
    // popupAnchor:  [45, 45],
    iconAnchor: [15,30],
    iconSize: [30,30],
});
const iconMarkerPin = new L.Icon({
    iconUrl: markerPin,
    iconRetinaUrl: markerPin,
    // popupAnchor:  [45, 45],
    iconAnchor: [30,60],
    iconSize: [60,60],
});
const iconCircle = new L.Icon({
    iconUrl: markerCircleSvg,
    iconRetinaUrl: markerCircleSvg,
    // popupAnchor:  [45, 45],
    iconAnchor: [15,15],
    iconSize: [15,15],
});
const iconSnowmobile = new L.Icon({
    iconUrl: markerSnowMobileSvg,
    iconRetinaUrl: markerSnowMobileSvg,
    // popupAnchor:  [45, 45],
    iconAnchor: [25,25],
    iconSize: [25,25],
});

const fieldSorter = (fields) => (a, b) => fields.map(o => {
    let dir = 1;
    if (o[0] === '-') {
        dir = -1;
        o = o.substring(1);
    }
    return a[o] > b[o] ? dir : a[o] < b[o] ? -(dir) : 0;
}).reduce((p, n) => p ? p : n, 0);

const ResizeMap = () => {
    const map = useMap();
    map._onResize();
    return null;
};

const ZoomTracker = ({ onZoomChange }) => {
    const map = useMapEvents({
        zoomend: () => {
            onZoomChange(map.getZoom());
        },
    });
    return null;
};

function getDistanceInterval(zoom) {
    if (zoom >= 13) return 5000;
    if (zoom >= 11) return 10000;
    if (zoom >= 9) return 25000;
    if (zoom >= 7) return 50000;
    return 100000;
}

const ChangeView = ({ center, zoom, bounds = [] }) => {
    const map = useMap();
    map.setView(center, zoom);
    if (bounds.length > 0) {
        map.fitBounds(bounds);
    }
    return null;
}

const FitBounds = ({ bounds = [] }) => {
    const map = useMap();
    if (bounds.length > 0) {
        map.fitBounds(bounds);
    }
    return null;
}

const COLOR_GSM = '#2196F3';
const COLOR_SAT = '#f58a07';
const COLOR_MIXED = '#9C27B0';

class DebugGpsClusterWrapper extends Component {
    constructor(props) {
        super(props);
        this.cleanupRef = null;
    }

    componentWillUnmount() {
        if (this.cleanupRef && this.cleanupRef._cleanup) {
            this.cleanupRef._cleanup();
        }
    }

    render() {
        const { points } = this.props;
        return h(DebugGpsClusterLayerInner, {
            points,
            onRef: (el) => { this.cleanupRef = el; },
        });
    }
}

function DebugGpsClusterLayerInner({ points, onRef }) {
    const map = useMap();

    // Use map-attached storage to persist across renders and avoid listener accumulation
    if (!map._debugGpsState) {
        map._debugGpsState = { markers: [], handler: null };
    }
    const state = map._debugGpsState;

    function clearAllMarkers() {
        while (state.markers.length > 0) {
            map.removeLayer(state.markers.pop());
        }
    }

    function doBuildClusters() {
        clearAllMarkers();
        const bounds = map.getBounds();
        const cellSize = 60;
        const cells = {};
        const pts = map._debugGpsPoints || [];

        for (let i = 0; i < pts.length; i++) {
            const p = pts[i];
            if (!bounds.contains([p.lat, p.lng])) continue;
            const pt = map.latLngToContainerPoint([p.lat, p.lng]);
            const key = `${Math.floor(pt.x / cellSize)}_${Math.floor(pt.y / cellSize)}`;
            if (!cells[key]) cells[key] = [];
            cells[key].push(p);
        }

        Object.values(cells).forEach(group => {
            const count = group.length;
            const rep = group[0];

            if (count === 1) {
                const color = rep.type === 'sat' ? COLOR_SAT : COLOR_GSM;
                const marker = L.circleMarker([rep.lat, rep.lng], {
                    radius: 5,
                    color,
                    fillColor: color,
                    fillOpacity: 0.8,
                    weight: 1,
                });
                marker.bindPopup(`<div style="font-size:12px;">
                    <strong>${rep.type === 'sat' ? 'SAT' : 'GSM'} Point</strong><br/>
                    Tracker: ${rep.trackerId}<br/>
                    Time: ${rep.timestamp ? new Date(rep.timestamp).toLocaleString() : 'N/A'}<br/>
                    Speed: ${rep.speed != null ? rep.speed + ' km/h' : 'N/A'}<br/>
                    Battery: ${rep.battery || 'N/A'}<br/>
                    Elevation: ${rep.elevation ? Math.round(rep.elevation) + 'm' : 'N/A'}
                </div>`);
                marker.addTo(map);
                state.markers.push(marker);
            } else {
                const satCount = group.filter(p => p.type === 'sat').length;
                const gsmCount = count - satCount;
                let color = COLOR_MIXED;
                if (satCount === 0) color = COLOR_GSM;
                else if (gsmCount === 0) color = COLOR_SAT;

                let latSum = 0, lngSum = 0;
                for (let i = 0; i < count; i++) {
                    latSum += group[i].lat;
                    lngSum += group[i].lng;
                }
                const avgLat = latSum / count;
                const avgLng = lngSum / count;

                const size = Math.min(18 + Math.log2(count) * 8, 50);
                const icon = L.divIcon({
                    className: '',
                    iconSize: [size, size],
                    iconAnchor: [size / 2, size / 2],
                    html: `<div style="
                        width:${size}px;height:${size}px;
                        background:${color};
                        border:2px solid #fff;
                        border-radius:50%;
                        color:#fff;
                        font-size:${Math.max(10, size * 0.4)}px;
                        font-weight:bold;
                        display:flex;
                        align-items:center;
                        justify-content:center;
                        box-shadow:0 1px 4px rgba(0,0,0,0.4);
                        cursor:pointer;
                    ">${count}</div>`,
                });

                const marker = L.marker([avgLat, avgLng], { icon });
                marker.on('click', () => {
                    const currentZoom = map.getZoom();
                    if (currentZoom < 12) {
                        map.setView([avgLat, avgLng], Math.min(currentZoom + 3, 18));
                    } else {
                        const clusterBounds = L.latLngBounds(group.map(p => [p.lat, p.lng]));
                        const ne = clusterBounds.getNorthEast();
                        const sw = clusterBounds.getSouthWest();
                        const sameSpot = Math.abs(ne.lat - sw.lat) < 0.00001 && Math.abs(ne.lng - sw.lng) < 0.00001;
                        if (sameSpot || currentZoom >= 18) {
                            map.setView([avgLat, avgLng], 18);
                        } else {
                            const targetZoom = map.getBoundsZoom(clusterBounds, false, [50, 50]);
                            if (targetZoom <= currentZoom + 1) {
                                map.setView([avgLat, avgLng], Math.min(currentZoom + 3, 18));
                            } else {
                                map.fitBounds(clusterBounds, { padding: [50, 50], maxZoom: 18 });
                            }
                        }
                    }
                });
                marker.addTo(map);
                state.markers.push(marker);
            }
        });
    }

    // Store points and build function on map so handler can access latest data
    map._debugGpsPoints = points;
    map._debugGpsBuild = doBuildClusters;

    // Only attach event listeners once
    if (!state.handler) {
        state.handler = () => {
            if (map._debugGpsBuild) map._debugGpsBuild();
        };
        map.on('zoomend', state.handler);
        map.on('moveend', state.handler);
    }

    // Rebuild clusters on each render (points may have changed)
    doBuildClusters();

    return h('div', {
        style: 'display:none;',
        ref: (el) => {
            if (onRef) onRef(el);
            if (el) {
                el._cleanup = () => {
                    clearAllMarkers();
                    if (state.handler) {
                        map.off('zoomend', state.handler);
                        map.off('moveend', state.handler);
                    }
                    delete map._debugGpsState;
                    delete map._debugGpsPoints;
                    delete map._debugGpsBuild;
                };
            }
        },
    });
}

function getColor(idx) {
    const colors = [
        'rgb(255, 0, 0)',     // Red
        'rgb(0, 255, 0)',     // Green
        'rgb(0, 0, 255)',     // Blue
        'rgb(255, 255, 0)',   // Yellow
        'rgb(255, 0, 255)',   // Purple
        'rgb(0, 255, 255)',   // Cyan
        'rgb(255, 128, 0)',   // Orange
        'rgb(128, 0, 255)',   // Violet
        'rgb(255, 0, 128)',   // Pink
        'rgb(128, 255, 0)',   // Lime
        'rgb(0, 128, 255)',   // Azure
        'rgb(255, 0, 128)',   // Rose
        'rgb(0, 255, 128)',   // Spring green
        'rgb(255, 128, 128)',   // Salmon
          // Add more colors as needed
    ];

    const color = colors[parseInt(idx, 10) % colors.length];
    return color;
}

function* colorCycler() {
    const colorArray = [
        'rgb(54, 133, 252)',  // Original bright blue
        'rgb(84, 72, 252)',   // Deeper purple accent (analogous)
        'rgb(54, 54, 252)',   // Complementary blue
        'rgb(77, 189, 252)',  // Lighter sky blue (tone on tone)

        'rgb(54, 252, 54)',   // Complementary green
        'rgb(54, 252, 133)',  // Vivid green

        'rgb(252, 54, 54)',   // Complementary red
        'rgb(252, 54, 133)',  // Vivid magenta
        'rgb(252, 163, 54)',  // Complementary orange

        'rgb(34, 34, 34)',    // Dark neutral (text)
        'rgb(230, 230, 230)', // Light neutral (background)
    ];

    let index = 0;
    while (true) {
        yield colorArray[index];
        index = (index + 1) % colorArray.length;
    }
}

/**
 * Calculate distance between two coordinates in meters using Haversine formula
 */
function haversineDistance(lat1, lng1, lat2, lng2) {
    const R = 6371000; // Earth's radius in meters
    const toRad = deg => (deg * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
              Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}

/**
 * Extract arrow positions from GeoJSON LineString features
 * @param {Object} geoJSON - GeoJSON object
 * @param {number} intervalMeters - Distance between arrows in meters (default 2000m = 2km)
 * @returns {Array} - Array of { lat, lng, bearing, distanceKm } objects
 */
function getArrowPositions(geoJSON, intervalMeters = 2000) {
    if (!geoJSON || !geoJSON.features) return [];

    const arrows = [];
    let totalDistance = 0;
    let nextArrowAt = intervalMeters; // First arrow at the interval distance

    // Extract all LineString coordinates
    geoJSON.features.forEach(feature => {
        if (!feature.geometry) return;

        let coordinateArrays = [];
        if (feature.geometry.type === 'LineString') {
            coordinateArrays = [feature.geometry.coordinates];
        } else if (feature.geometry.type === 'MultiLineString') {
            coordinateArrays = feature.geometry.coordinates;
        }

        coordinateArrays.forEach(coords => {
            for (let i = 0; i < coords.length - 1; i++) {
                const [lng1, lat1] = coords[i];
                const [lng2, lat2] = coords[i + 1];

                const segmentDistance = haversineDistance(lat1, lng1, lat2, lng2);
                const segmentEndDistance = totalDistance + segmentDistance;

                // Place arrows within this segment
                while (nextArrowAt <= segmentEndDistance) {
                    // Distance from segment start to arrow position
                    const distanceIntoSegment = nextArrowAt - totalDistance;

                    // Interpolate position along segment
                    const ratio = distanceIntoSegment / segmentDistance;
                    const arrowLat = lat1 + (lat2 - lat1) * ratio;
                    const arrowLng = lng1 + (lng2 - lng1) * ratio;

                    // Calculate bearing from this point to next
                    const bearing = calculateBearing(lat1, lng1, lat2, lng2);

                    arrows.push({
                        lat: arrowLat,
                        lng: arrowLng,
                        bearing,
                        distanceKm: nextArrowAt / 1000, // Distance in km
                    });

                    nextArrowAt += intervalMeters;
                }

                totalDistance = segmentEndDistance;
            }
        });
    });

    return arrows;
}

/**
 * Calculate total track length from GeoJSON in meters
 * @param {Object} geoJSON - GeoJSON object
 * @returns {number} - Total length in meters
 */
function getTrackLength(geoJSON) {
    if (!geoJSON || !geoJSON.features) return 0;

    let totalDistance = 0;
    geoJSON.features.forEach(feature => {
        if (!feature.geometry) return;
        let coordinateArrays = [];
        if (feature.geometry.type === 'LineString') {
            coordinateArrays = [feature.geometry.coordinates];
        } else if (feature.geometry.type === 'MultiLineString') {
            coordinateArrays = feature.geometry.coordinates;
        }
        coordinateArrays.forEach(coords => {
            for (let i = 0; i < coords.length - 1; i++) {
                const [lng1, lat1] = coords[i];
                const [lng2, lat2] = coords[i + 1];
                totalDistance += haversineDistance(lat1, lng1, lat2, lng2);
            }
        });
    });
    return totalDistance;
}

// Map Yr weather symbol codes to FontAwesome icons
function getWeatherIcon(symbolCode) {
    if (!symbolCode) return null;
    const code = symbolCode.toLowerCase();
    if (code.includes('snow') || code.includes('sleet')) return 'fa-snowflake';
    if (code.includes('rain') || code.includes('drizzle')) return 'fa-cloud-rain';
    if (code.includes('thunder')) return 'fa-cloud-bolt';
    if (code.includes('fog')) return 'fa-smog';
    if (code.includes('clearsky')) return 'fa-sun';
    if (code.includes('fair')) return 'fa-cloud-sun';
    if (code.includes('partlycloudy')) return 'fa-cloud-sun';
    if (code.includes('cloudy')) return 'fa-cloud';
    return 'fa-cloud'; // default
}

// Convert wind direction degrees to cardinal direction
function getWindDirection(degrees) {
    if (degrees == null) return '';
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(degrees / 45) % 8;
    return directions[index];
}

// Split GPS points into segments, marking gaps > threshold
// Used to visualize GPS coverage gaps with red dashed lines
function splitPointsByGaps(points, gapThresholdMs = 900000) {
    if (!points || points.length < 2) {
        return [{ points: points || [], isGap: false }];
    }

    const segments = [];
    let currentSegment = [points[0]];

    for (let i = 1; i < points.length; i++) {
        const prevTimestamp = new Date(points[i - 1][2]).getTime();
        const currTimestamp = new Date(points[i][2]).getTime();
        const gap = currTimestamp - prevTimestamp;

        if (gap > gapThresholdMs) {
            // End current segment
            if (currentSegment.length > 0) {
                segments.push({ points: currentSegment, isGap: false });
            }
            // Add gap segment (just the two endpoints)
            segments.push({
                points: [points[i - 1], points[i]],
                isGap: true,
                gapSeconds: Math.round(gap / 1000)
            });
            // Start new segment
            currentSegment = [points[i]];
        } else {
            currentSegment.push(points[i]);
        }
    }

    // Don't forget the last segment
    if (currentSegment.length > 0) {
        segments.push({ points: currentSegment, isGap: false });
    }

    return segments;
}

function splitSegmentsByType(segments) {
    const result = [];
    for (const segment of segments) {
        if (segment.isGap) {
            result.push(segment);
            continue;
        }
        const points = segment.points;
        if (!points || points.length === 0) {
            result.push(segment);
            continue;
        }
        let currentType = points[0][6] || '';
        let currentPoints = [points[0]];

        for (let i = 1; i < points.length; i++) {
            const type = points[i][6] || '';
            if (type !== currentType) {
                // Include overlapping point for continuity
                result.push({ points: currentPoints, isGap: false, pointType: currentType });
                currentType = type;
                currentPoints = [points[i - 1], points[i]]; // overlap for connected line
            } else {
                currentPoints.push(points[i]);
            }
        }
        if (currentPoints.length > 0) {
            result.push({ points: currentPoints, isGap: false, pointType: currentType });
        }
    }
    return result;
}

const geofenceArrivalSound = new Audio('../../assets/sounds/new2.mp3');
const geofenceDepartureSound = new Audio('../../assets/sounds/send.mp3');

@withText(props => ({
    titleText: <Text id='race-class-contestants.title'>Race Contestants</Text>,
}))
@observer
class RaceClassContestantList extends Component {
  	constructor(props) {
        super(props);
        this.state = {
            rcId: props.raceClass?.id,
            // Replay state
            replayMode: false,
            replayPlaying: false,
            replayCurrentTime: null,
            replaySpeed: 25, // Default to 25x speed for long races
            replayStartTime: null,
            replayEndTime: null,
            replayTrackers: [],
            isLoadingReplay: false, // Flag to prevent marker rendering during replay load
            // Elevation state
            showElevationProfile: false,
            elevationStats: {}, // { contestantId: { totalGain, totalLoss, minElevation, maxElevation, currentElevation } }
            elevationProfile: [], // Combined elevation profile for selected tracker or all
            selectedTrackerId: null, // For elevation profile display
            // Mobile UI state
            mobileMenuOpen: false,
            // Auto-update state
            autoUpdate: true,
            autoUpdateCountdown: 60,
            currentZoom: 12,
            // Geofence edit mode
            editGeofences: false,
            showGeofences: false,
            editingCheckpointMd5: null,
            pendingGeofenceArrival: null,
            pendingGeofenceDeparture: null,
            // Tracker warnings
            showTrackerWarnings: false,
            // Real GPS points only (disable interpolation)
            showRealPointsOnly: false,
            // Track weather along GPX route
            trackWeather: [],
            // Last weather fetch time for replay (to avoid fetching too often)
            lastWeatherFetchReplayTime: null,
            // Real-time rate limit for weather fetches during replay
            lastWeatherFetchRealTime: null,
            // Geofence sound
            geofenceSoundEnabled: false,
            // All satellite points overlay (admin)
            showAllSatPoints: false,
            allSatPoints: [],
            allSatPointsLoaded: false,
            loadingAllSatPoints: false,
            // Debug GPS points overlay (admin) - all GSM + SAT points with clustering
            showDebugGpsPoints: false,
            debugGpsPoints: [],
            debugGpsPointsLoaded: false,
            loadingDebugGpsPoints: false,
            // Mobile coverage overlay (public)
            showCoverage: false,
        };
        this.mapRef = createRef;
        this.replayFrameId = null;
        this.lastTickTimestamp = null;
        this.countdownIntervalId = null;
        this.weatherRefreshInterval = null;
        this.weatherFetchDebounceTimer = null;
        // Session tracking for analytics
        this.viewSessionId = null;
        this.viewStartTime = null;
        this.viewingHeartbeatTimer = null;
        // Geofence sound dedup
        this.lastGeofenceEventTimestamp = null;
    }

    viewRaceClassCheckpoint = e => {
        const {
            race,
            raceClass,
            raceClassContestants,
        } = this.props;

        const { md5 } = e.target.closest('.cp-line').dataset;
        const { appState } = this.props.stores;
        const { drawerLevel = 1 } = this.props;
        const { drawerHeightMedium } = appState;
        // console.log('viewPart', { id });
        appState.openDrawer('viewRaceClassCheckpoint', {
            md5,
            height: drawerHeightMedium,
            editFunction: this.editMediaViaView,
            race,
            raceClass,
            raceClassContestants,
            callback: () => {
                appState.toggleDrawer();
                // this.loadAll();
            },
        }, drawerLevel + 1);
    }

    componentDidUpdate(prevProps, prevState) {
        const { replayMode, replayCurrentTime, lastWeatherFetchReplayTime, lastWeatherFetchRealTime } = this.state;

        // In replay mode, refresh weather when time changes by 15 minutes or more
        // Debounce to wait for scrubbing to settle, and rate limit to max 1 per minute
        if (replayMode && replayCurrentTime) {
            const fifteenMinutes = 15 * 60 * 1000;
            const oneMinute = 60 * 1000;

            const replayTimeChanged = !lastWeatherFetchReplayTime
                || Math.abs(replayCurrentTime - lastWeatherFetchReplayTime) >= fifteenMinutes;

            if (replayTimeChanged) {
                // Clear any pending debounce timer
                if (this.weatherFetchDebounceTimer) {
                    clearTimeout(this.weatherFetchDebounceTimer);
                }

                // Debounce: wait 500ms after scrubbing stops
                this.weatherFetchDebounceTimer = setTimeout(() => {
                    const now = Date.now();
                    const { lastWeatherFetchRealTime: currentRealTime } = this.state;

                    // Rate limit: max 1 fetch per minute of real time
                    const realTimeOk = !currentRealTime || (now - currentRealTime) >= oneMinute;

                    if (realTimeOk) {
                        this.setState({
                            lastWeatherFetchReplayTime: this.state.replayCurrentTime,
                            lastWeatherFetchRealTime: now,
                        }, () => {
                            this.fetchTrackWeather(this.state.replayCurrentTime);
                        });
                    }
                }, 500);
            }
        }

        // When exiting replay mode, reset tracking and clear debounce
        if (prevState.replayMode && !replayMode) {
            if (this.weatherFetchDebounceTimer) {
                clearTimeout(this.weatherFetchDebounceTimer);
                this.weatherFetchDebounceTimer = null;
            }
            this.setState({
                lastWeatherFetchReplayTime: null,
                lastWeatherFetchRealTime: null,
            });
        }
    }

    fetchTrackWeather = async (timestamp = null) => {
        const { raceClass } = this.props;
        const { gpsPointStore } = this.props.stores;
        const { files } = raceClass || {};
        const { replayMode } = this.state;

        // console.log('fetchTrackWeather: files=', files?.length, 'geoJSON=', !!files?.[0]?.geoJSON, 'replayMode=', replayMode, 'timestamp=', timestamp);

        if (!files || files.length === 0) return;

        const geoJSON = files[0]?.geoJSON;
        if (!geoJSON) return;

        // Cap the number of weather points to avoid excessive API calls
        const MAX_WEATHER_POINTS = 15;
        const MIN_INTERVAL = 20000; // 20km minimum

        // Calculate track length to determine optimal interval
        const trackLength = getTrackLength(geoJSON);
        const calculatedInterval = trackLength > 0 ? Math.ceil(trackLength / MAX_WEATHER_POINTS) : MIN_INTERVAL;
        const interval = Math.max(MIN_INTERVAL, calculatedInterval);

        // Get positions at calculated intervals along the track, hard-capped at MAX_WEATHER_POINTS
        const allWeatherPositions = getArrowPositions(geoJSON, interval);
        const weatherPositions = allWeatherPositions.slice(0, MAX_WEATHER_POINTS);
        console.log('fetchTrackWeather: trackLength=', Math.round(trackLength / 1000), 'km, interval=', Math.round(interval / 1000), 'km, positions=', weatherPositions.length);
        if (weatherPositions.length === 0) return;

        // Fetch weather for each position
        const trackWeatherData = [];
        for (const pos of weatherPositions) {
            try {
                let result;
                if (replayMode && timestamp) {
                    // In replay mode, fetch historical weather
                    result = await gpsPointStore.getYrWeatherHistorical({
                        lat: pos.lat,
                        lon: pos.lng,
                        timestamp,
                    });
                } else {
                    // In live mode, fetch current weather
                    result = await gpsPointStore.getYrWeather({
                        lat: pos.lat,
                        lon: pos.lng,
                        altitude: 500,
                    });
                }
                if (result?.data?.[0]) {
                    trackWeatherData.push({
                        lat: pos.lat,
                        lng: pos.lng,
                        distanceKm: pos.distanceKm,
                        weather: result.data[0],
                    });
                }
            } catch (err) {
                console.error('Failed to fetch weather for position:', pos, err);
            }
        }

        // console.log('fetchTrackWeather: trackWeatherData=', trackWeatherData.length, trackWeatherData);
        this.setState({ trackWeather: trackWeatherData });
    }

    editWaypoint = (md5, waypointType = 'raceClass') => {
        const { race, raceClass, drawerLevel = 1 } = this.props;
        const { appState, raceClassStore, raceStore } = this.props.stores;
        const { drawerHeightMedium } = appState;

        const holdingStore = waypointType === 'race' ? raceStore : raceClassStore;
        const object = waypointType === 'race' ? race : raceClass;

        // Find the waypoint and pre-set it on the store (fixes race condition in editArrayObject)
        const waypoints = object.waypoints || [];
        const waypoint = waypoints.find(wp => wp.md5 === md5);
        if (!waypoint) {
            return;
        }
        holdingStore.updateKeyValue('newObject', waypoint);

        appState.openDrawer('editArrayObject', {
            height: drawerHeightMedium,
            datalist: ['name', 'lat', 'lng', 'rotateDeg', 'icon'],
            datalistHelp: {
                name: 'Name of the waypoint',
                lat: 'Latitude coordinate',
                lng: 'Longitude coordinate',
                rotateDeg: 'Rotation angle in degrees',
                icon: 'FontAwesome icon class (e.g., fa-solid fa-flag)',
            },
            fieldObject: {},
            object,
            objectName: waypointType === 'race' ? 'race' : 'raceClass',
            field: 'waypoints',
            md5,
            holdingStore,
            isAdmin: true,
            callback: () => {
                appState.toggleDrawer();
            },
        }, drawerLevel + 1);
    }

    addWaypoint = (waypointType = 'raceClass') => {
        const { race, raceClass, drawerLevel = 1 } = this.props;
        const { appState, raceClassStore, raceStore } = this.props.stores;
        const { drawerHeightMedium } = appState;

        const holdingStore = waypointType === 'race' ? raceStore : raceClassStore;
        const object = waypointType === 'race' ? race : raceClass;

        // Get default coordinates from first checkpoint if available
        const checkpoints = object.checkpoints || [];
        const defaultLat = checkpoints[0]?.lat || 69.0;
        const defaultLng = checkpoints[0]?.lng || 18.0;

        // Generate a new md5 for the waypoint
        const newMd5 = `wp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        // Create a new waypoint object and pre-set it on the store
        const newWaypoint = {
            md5: newMd5,
            name: 'New Waypoint',
            lat: defaultLat,
            lng: defaultLng,
            icon: '',
        };
        holdingStore.updateKeyValue('newObject', newWaypoint);

        appState.openDrawer('editArrayObject', {
            height: drawerHeightMedium,
            datalist: ['name', 'lat', 'lng', 'rotateDeg', 'icon'],
            datalistHelp: {
                name: 'Name of the waypoint',
                lat: 'Latitude coordinate',
                lng: 'Longitude coordinate',
                rotateDeg: 'Rotation angle in degrees',
                icon: 'FontAwesome icon class (e.g., fa-solid fa-flag)',
            },
            fieldObject: {},
            object,
            objectName: waypointType === 'race' ? 'race' : 'raceClass',
            field: 'waypoints',
            md5: newMd5,
            isNew: true,
            holdingStore,
            isAdmin: true,
            callback: () => {
                appState.toggleDrawer();
            },
        }, drawerLevel + 1);
    }

    loadGpsPoints = async () => {
        const { skipRaceClass } = this.props;
        const { cutoffDate = '', replayMode, isLoadingReplay } = this.state;
        const {
            race,
            raceClass,
            raceClassContestants,
        } = this.props;
        if (!race || !race.id) {
            return;
        }
        // Don't let live polling (firebase debounce, cutoff changes) merge live
        // data into the store while replay data is loading or being replayed
        if (replayMode || isLoadingReplay) {
            return;
        }
        const { gpsPointStore } = this.props.stores;
        await gpsPointStore.getGpsPoints({
            race: race.id,
            raceClass: skipRaceClass ? '' : raceClass.id,
            cutoffDate,
        });
    }

    loadRaceClassContestants = async (props = this.props) => {
		const { raceStore, raceClassStore, raceClassContestantStore, userStore } = props.stores;
        const { skipRaceClass, rcId } = this.state;

        // raceClassStore.updateKeyValue('raceClass', {});

        await raceClassStore.load(rcId, false, {
            url: '/api/raceclasses/public/',
            addData: ['files'],
        });

        const { race } = raceStore;
        await raceClassContestantStore.load({
            query: {
                race: race.id,
                raceClass: skipRaceClass ? null : rcId,
                addResults: true,
            },
            url: '/api/raceclasscontestants/public/',
        });

        const { user } = userStore;
        raceStore.checkAllPositions(user);
    }

    loadAll = async (props = this.props, fitBounds = true) => {
        const {
            race,
            raceClass,
            raceClassContestants,
            inputMarkers,
        } = this.props;
        const { skipRaceClass, rcId } = this.state;

        const { files } = raceClass;
        const { gpsPointStore } = this.props.stores;

        this.stopReloadTimer();
        // this.stopReloadTimer();
        if (race && race.id) {
            await gpsPointStore.getGpsPoints({ race: race.id, raceClass: skipRaceClass ? '' : rcId });
        }

        // Calculate elevation profile from GPX route (not tracker points)
        const { trackers } = gpsPointStore;
        this.calculateRouteElevationProfile(files);
        this.calculateElevationData(trackers);

        // const map = this.mapRef.current.leafletElement;
        if (fitBounds && this.mapRef) {
            let { bounds } = gpsPointStore;
            // console.log(bounds);

            if (!bounds || bounds.length === 0) {
                if (files && files.length > 0) {
                    const geoJsonLayer = L.geoJSON(files[0].geoJSON);
                    this.mapRef.fitBounds(geoJsonLayer.getBounds(), { padding: [100, 130] });
                }
            }

            // const geoJsonLayer = L.geoJSON(this.props.geoJsonData);
            // map.leafletElement.fitBounds(geoJsonLayer.getBounds());

            if (bounds.length > 0) {
                let markerBounds = L.latLngBounds([]);
                bounds.forEach(b => {
                    markerBounds.extend(b);
                });
                if (this.mapRef) {
                    this.mapRef.fitBounds(markerBounds, { padding: [100, 140] });
                }
            }

            if (inputMarkers && inputMarkers.length > 0) {
                if (inputMarkers.length === 1) {
                    if (this.mapRef) {
                        this.mapRef.setView(inputMarkers[0], 9);
                    }
                } else {
                    let markerBounds = L.latLngBounds([]);
                    inputMarkers.forEach(marker => {
                        markerBounds.extend(marker);
                    });
                    if (this.mapRef) {
                        this.mapRef.fitBounds(markerBounds, { padding: [100, 140] });
                    }
                }
            }
        }
        // gpsPointStore.setLastPoint();
        // gpsPointStore.setPolyline();

        this.startReloadTimer();

        // Fetch weather for track positions (files are now loaded)
        this.fetchTrackWeather();
    }

    calculateRouteElevationProfile = (files) => {
        if (!files || files.length === 0) return;

        const geoJSON = files[0]?.geoJSON;
        if (!geoJSON || !geoJSON.features) return;

        // Extract coordinates from LineString features
        const lineFeatures = geoJSON.features.filter(f =>
            f.geometry?.type === 'LineString' || f.geometry?.type === 'MultiLineString'
        );

        if (lineFeatures.length === 0) return;

        // Collect all coordinates from all LineString features
        // Using push(...) instead of concat() for O(n) performance with large datasets
        let allCoordinates = [];
        lineFeatures.forEach(feature => {
            if (feature.geometry.type === 'LineString') {
                allCoordinates.push(...feature.geometry.coordinates);
            } else if (feature.geometry.type === 'MultiLineString') {
                feature.geometry.coordinates.forEach(line => {
                    allCoordinates.push(...line);
                });
            }
        });

        // Check if coordinates have elevation (3rd element)
        const hasElevation = allCoordinates.some(c => c.length >= 3 && c[2] > 0);
        if (!hasElevation) return;

        // Convert GeoJSON coordinates [lng, lat, elev] to GPS points format
        const gpsPoints = allCoordinates
            .filter(c => c.length >= 3 && c[2] > 0)
            .map(c => ({
                lat: c[1],  // GeoJSON is [lng, lat, elev]
                lng: c[0],
                elevation: c[2],
            }));

        if (gpsPoints.length === 0) return;

        // Calculate elevation profile
        const profile = calculateElevationProfile(gpsPoints);
        const downsampledProfile = downsampleProfile(profile, 750);

        this.setState({
            elevationProfile: downsampledProfile,
        });
    }

    calculateElevationData = (trackers) => {
        if (!trackers || trackers.length === 0) return;

        const elevationStats = {};

        trackers.forEach(tracker => {
            if (!tracker.points || tracker.points.length === 0) return;

            // Convert points to format expected by elevation utils
            // Points are [lat, lng, epoch, speed, battery, elevation]
            const gpsPoints = tracker.points.map(p => ({
                lat: p[0],
                lng: p[1],
                elevation: p[5] || 0, // elevation is at index 5
                timestamp: p[2],
            })).filter(p => p.elevation > 0); // Filter out points without elevation

            if (gpsPoints.length > 0) {
                const stats = calculateElevationStats(gpsPoints);
                const contestantId = tracker.contestant?.id || tracker.trackerId;
                elevationStats[contestantId] = stats;
            }
        });

        // Only update stats, don't overwrite the route elevation profile
        this.setState({
            elevationStats,
        });
    }

    setupFirebaseGpsListener = () => {
        const { race } = this.props;
        if (!race || !race.id) {
            return;
        }
        try {
            const docRef = doc(firestoreDb, 'gpsTracking', `race_${race.id}`);
            const { userStore } = this.props.stores;
            if (userStore?.isAdmin) console.log(`[GPS Firebase] Listener set up for race_${race.id}`);
            this.firebaseUnsubscribe = onSnapshot(docRef, (snapshot) => {
                const data = snapshot.data() || {};
                if (userStore?.isAdmin) console.log(`[GPS Firebase] Update received — trackerId: ${data.trackerId}, raceClass: ${data.raceClass}, lastUpdate: ${data.lastUpdate?.toDate?.() || data.lastUpdate}`);

                // Check for geofence event (play sound immediately, no debounce)
                const { raceStore } = this.props.stores;
                if ((userStore?.isAdmin || raceStore?.isRaceAdmin) && data.geofenceEventTimestamp && data.geofenceEvent) {
                    const eventTs = data.geofenceEventTimestamp?.seconds || 0;
                    if (eventTs !== this.lastGeofenceEventTimestamp) {
                        this.lastGeofenceEventTimestamp = eventTs;
                        const { geofenceSoundEnabled } = this.state;
                        if (geofenceSoundEnabled) {
                            if (data.geofenceEvent === 'arrival') {
                                geofenceArrivalSound.play().catch(err => console.log('Geofence sound play failed:', err));
                            } else {
                                geofenceDepartureSound.play().catch(err => console.log('Geofence sound play failed:', err));
                            }
                        }
                    }
                }

                // Try to use actual GPS data from Firebase for immediate update
                if (data.lat && data.lng) {
                    const { gpsPointStore } = this.props.stores;
                    const updated = gpsPointStore.updateTrackerFromFirebase(data);
                    if (userStore?.isAdmin) console.log(`[GPS Firebase] Immediate update — lat: ${data.lat}, lng: ${data.lng}, applied: ${updated}`);

                    if (!updated) {
                        // Unknown tracker — do a full API refresh soon
                        if (this.firebaseGpsDebounceTimer) {
                            clearTimeout(this.firebaseGpsDebounceTimer);
                        }
                        this.firebaseGpsDebounceTimer = setTimeout(() => {
                            if (userStore?.isAdmin) console.log('[GPS Firebase] Unknown tracker, full API refresh...');
                            this.loadGpsPoints();
                        }, 3000);
                        return;
                    }

                    // Still do periodic full API sync but at a longer interval
                    if (this.firebaseGpsDebounceTimer) {
                        clearTimeout(this.firebaseGpsDebounceTimer);
                    }
                    this.firebaseGpsDebounceTimer = setTimeout(() => {
                        if (userStore?.isAdmin) console.log('[GPS Firebase] Periodic full sync...');
                        this.loadGpsPoints();
                    }, 30000);
                } else {
                    // Signal-only (no GPS data) — fall back to debounced API refresh
                    if (this.firebaseGpsDebounceTimer) {
                        clearTimeout(this.firebaseGpsDebounceTimer);
                    }
                    this.firebaseGpsDebounceTimer = setTimeout(() => {
                        if (userStore?.isAdmin) console.log('[GPS Firebase] Signal-only, loading GPS points...');
                        this.loadGpsPoints();
                    }, 3000);
                }
            }, (error) => {
                console.warn('[GPS Firebase] onSnapshot error:', error.code, error.message);
            });
        } catch (err) {
            console.warn('[GPS Firebase] Failed to set up listener:', err.message);
        }
    }

    startReloadTimer = () => {
        const { autoUpdate, replayMode } = this.state;
        // Only start timer when auto-update is on and not in replay mode
        if (!autoUpdate || replayMode) {
            return;
        }
        const { raceStore } = this.props.stores;

        // Reset countdown
        this.setState({ autoUpdateCountdown: 30 });

        // Start countdown interval
        if (this.countdownIntervalId) {
            clearInterval(this.countdownIntervalId);
        }
        this.countdownIntervalId = setInterval(() => {
            this.setState(prevState => ({
                autoUpdateCountdown: Math.max(0, prevState.autoUpdateCountdown - 1)
            }));
        }, 1000);

        const timer = setTimeout(async () => {
            // Clear any pending Firebase debounce to prevent racing
            if (this.firebaseGpsDebounceTimer) {
                clearTimeout(this.firebaseGpsDebounceTimer);
                this.firebaseGpsDebounceTimer = null;
            }
            // Fetch from liverace.no first, then reload local data with merge
            await this.fetchLiveraceData();
            this.loadAll(this.props, false);
        }, 1000 * 30);
        raceStore.setTrackingReloadTimer(timer);
    }

    stopReloadTimer = () => {
        const { raceStore } = this.props.stores;
        raceStore.deleteTrackingReloadTimer();
        // Stop countdown interval
        if (this.countdownIntervalId) {
            clearInterval(this.countdownIntervalId);
            this.countdownIntervalId = null;
        }
    }

    fetchLiveraceData = async () => {
        const { race, raceClass } = this.props;
        const { skipRaceClass, rcId, replayMode, autoUpdate } = this.state;

        // Only fetch when auto-update is on and not in replay mode
        if (!autoUpdate || replayMode) {
            return;
        }

        if (!race?.id) {
            return;
        }

        const raceClassId = skipRaceClass ? '' : (rcId || raceClass?.id || '');

        try {
            const response = await fetch(`https://www.liverace.no/api/gpspoints/group?race=${race.id}&raceClass=${raceClassId}&cutoffDate=`, {
                credentials: 'include',
                method: 'GET',
                mode: 'cors',
                headers: {
                    'Accept': 'application/json',
                },
            });

            if (response.ok) {
                const data = await response.json();
                // Update the store with fresh data from liverace.no
                const { gpsPointStore } = this.props.stores;
                if (data && data.data) {
                    // Merge with existing trackers — keep newer Firebase positions
                    const existingTrackers = gpsPointStore.trackers || [];
                    const apiTrackerMap = new Map(data.data.map(t => [t.trackerId, t]));
                    let hasChanges = false;
                    const mergedTrackers = existingTrackers.map(existingTracker => {
                        const apiTracker = apiTrackerMap.get(existingTracker.trackerId);
                        if (!apiTracker) {
                            return existingTracker;
                        }
                        const existingTs = existingTracker.timestamp ? new Date(existingTracker.timestamp).getTime() : 0;
                        const apiTs = apiTracker.timestamp ? new Date(apiTracker.timestamp).getTime() : 0;
                        if (!isNaN(existingTs) && existingTs > 0 && (isNaN(apiTs) || existingTs >= apiTs)) {
                            console.log(`[GPS Merge] source=liverace.no trackerId=${apiTracker.trackerId} existingTs=${existingTs} apiTs=${apiTs} winner=existing`);
                            return existingTracker;
                        }
                        hasChanges = true;
                        console.log(`[GPS Merge] source=liverace.no trackerId=${apiTracker.trackerId} existingTs=${existingTs} apiTs=${apiTs} winner=api`);
                        if (existingTracker.points && existingTracker.points.length > (apiTracker.points || []).length) {
                            return { ...existingTracker, ...apiTracker, points: existingTracker.points };
                        }
                        return { ...existingTracker, ...apiTracker };
                    });
                    // Add API-only trackers (not yet in existing)
                    data.data.forEach(apiTracker => {
                        if (!existingTrackers.find(t => t.trackerId === apiTracker.trackerId)) {
                            mergedTrackers.push(apiTracker);
                            hasChanges = true;
                        }
                    });
                    console.log(`[GPS Merge] source=liverace.no trackerCount before=${existingTrackers.length} after=${mergedTrackers.length} hasChanges=${hasChanges}`);
                    if (hasChanges) {
                        gpsPointStore.updateKeyValue('trackers', mergedTrackers);
                        gpsPointStore.updateKeyValue('totalTrackLength', data.included?.totalTrackLength || 0);
                        gpsPointStore.setBounds();
                        gpsPointStore.setMarkers();
                    }
                }
            }
        } catch (err) {
            console.log('Liverace fetch error:', err.message);
        }
    }

    toggleAutoUpdate = () => {
        const { autoUpdate, replayMode } = this.state;
        const newAutoUpdate = !autoUpdate;

        if (newAutoUpdate && !replayMode) {
            // Start auto-refresh - set state first then start timer in callback
            this.setState({ autoUpdate: newAutoUpdate }, () => {
                this.fetchLiveraceData();
                this.startReloadTimer();
            });
        } else {
            // Stop auto-refresh
            this.stopReloadTimer();
            this.setState({ autoUpdate: newAutoUpdate });
        }
    }

    // Replay methods
    toggleReplayMode = async () => {
        const { replayMode } = this.state;
        if (!replayMode) {
            // Enter replay mode - load full historical data
            await this.loadFullGpsData();
        } else {
            // Exit replay mode
            this.stopReplayLoop();
            // Drop the full replay history from the store so live mode reloads a
            // fresh live-window dataset instead of rendering multi-day trails
            const { gpsPointStore } = this.props.stores;
            gpsPointStore.updateKeyValue('trackers', []);
            this.setState({
                replayMode: false,
                replayPlaying: false,
                replayCurrentTime: null,
                replayStartTime: null,
                replayEndTime: null,
                replayTrackers: [],
            });
            this.loadAll(); // Resume live mode
        }
    }

    loadFullGpsData = async () => {
        const { race, raceClass } = this.props;
        const { skipRaceClass, rcId } = this.state;
        const { gpsPointStore, raceClassStore } = this.props.stores;

        // Get the current race class (may be different from props if user changed it)
        const currentRaceClass = raceClassStore.raceClass || raceClass;

        // Set loading flag BEFORE loading to prevent marker rendering issues
        this.stopReloadTimer();
        // Cancel any pending firebase-debounced live refresh so it doesn't merge
        // live data while the replay payload is loading
        if (this.firebaseGpsDebounceTimer) {
            clearTimeout(this.firebaseGpsDebounceTimer);
            this.firebaseGpsDebounceTimer = null;
        }

        // Use a Promise to wait for setState to complete
        await new Promise(resolve => {
            this.setState({ isLoadingReplay: true }, resolve);
        });

        // Load GPS data without cutoff to get full history
        await gpsPointStore.getGpsPoints({
            race: race.id,
            raceClass: skipRaceClass ? '' : rcId,
            cutoffDate: '', // No cutoff - get all data
            replay: true, // Keep all points for replay
        });

        const { trackers } = gpsPointStore;

        // Hard cutoff: filter points to only those within raceClass start/end times
        const raceStartTime = currentRaceClass?.startDate ? new Date(currentRaceClass.startDate).getTime() : null;
        const raceEndTime = currentRaceClass?.endDate ? new Date(currentRaceClass.endDate).getTime() : null;

        // Filter tracker points to only include those within the race time window
        const filteredTrackers = trackers.map(tracker => {
            if (!tracker.points || tracker.points.length === 0) {
                // Copy so live/firebase store mutations can't reach replayTrackers
                return { ...tracker, points: [] };
            }

            const filteredPoints = tracker.points.filter(point => {
                const pointTime = typeof point[2] === 'number' ? point[2] :
                    (typeof point[2] === 'string' || point[2] instanceof Date) ? new Date(point[2]).getTime() : null;

                if (pointTime == null) return false;

                // Apply hard cutoff: only include points within race class window
                const afterStart = raceStartTime == null || pointTime >= raceStartTime;
                const beforeEnd = raceEndTime == null || pointTime <= raceEndTime;

                return afterStart && beforeEnd;
            });

            return {
                ...tracker,
                points: filteredPoints,
            };
        });

        // If the raceClass start/end window excluded every GPS point (misconfigured
        // dates), fall back to the unfiltered data instead of an empty replay
        let finalTrackers = filteredTrackers;
        const hasFilteredPoints = filteredTrackers.some(t => t.points && t.points.length > 0);
        const hasRawPoints = trackers.some(t => t.points && t.points.length > 0);
        if (!hasFilteredPoints && hasRawPoints) {
            console.warn('Replay: raceClass start/end window excluded all GPS points - using unfiltered data');
            finalTrackers = trackers.map(tracker => ({
                ...tracker,
                points: [...(tracker.points || [])],
            }));
        }

        // Time bounds from the actual GPS data so replay starts where the first
        // tracker starts; raceClass dates only as fallback. Starting at
        // raceClass.startDate rendered an empty map (every getPositionAtTime
        // returns null before a tracker's first point) until playback caught up.
        const dataBounds = calculateRaceTimeBounds(finalTrackers);
        const startTime = dataBounds.startTime || raceStartTime;
        const endTime = dataBounds.endTime || raceEndTime;

        if (startTime && endTime && (hasFilteredPoints || hasRawPoints)) {
            this.setState({
                isLoadingReplay: false,
                replayMode: true,
                replayPlaying: false,
                replayCurrentTime: startTime,
                replayStartTime: startTime,
                replayEndTime: endTime,
                replayTrackers: [...finalTrackers], // Use filtered trackers
            });
            // Zoom out to fit all tracker routes
            this.fitReplayBounds(finalTrackers);
        } else {
            console.warn('Replay: No valid time bounds found in GPS data');
            // Fully leave replay mode — class switches during replay keep
            // replayMode true while loading, and a dataless class would otherwise
            // strand the UI in an empty replay with no running live updates
            this.setState({
                isLoadingReplay: false,
                noReplayData: true,
                replayMode: false,
                replayPlaying: false,
                replayCurrentTime: null,
                replayStartTime: null,
                replayEndTime: null,
                replayTrackers: [],
            });
            // Clear the warning after 3 seconds
            setTimeout(() => {
                this.setState({ noReplayData: false });
            }, 3000);
            this.loadAll(); // Resume live mode
        }
    }

    fitReplayBounds = (trackers) => {
        if (!this.mapRef || !trackers || trackers.length === 0) return;

        // Collect all points from all trackers to calculate bounds
        let bounds = L.latLngBounds([]);
        trackers.forEach(tracker => {
            if (tracker.points && tracker.points.length > 0) {
                tracker.points.forEach(point => {
                    if (typeof point[0] === 'number' && typeof point[1] === 'number' &&
                        isFinite(point[0]) && isFinite(point[1])) {
                        bounds.extend([point[0], point[1]]);
                    }
                });
            }
        });

        if (bounds.isValid()) {
            this.mapRef.fitBounds(bounds, { padding: [50, 50] });
        }
    }

    startReplayLoop = () => {
        this.lastTickTimestamp = performance.now();
        this.replayFrameId = requestAnimationFrame(this.replayTick);
    }

    stopReplayLoop = () => {
        if (this.replayFrameId) {
            cancelAnimationFrame(this.replayFrameId);
            this.replayFrameId = null;
        }
    }

    replayTick = (timestamp) => {
        const { replayPlaying, replayCurrentTime, replaySpeed, replayEndTime } = this.state;

        if (!replayPlaying) {
            return;
        }

        // Check if we've reached the end
        if (replayCurrentTime >= replayEndTime) {
            this.setState({ replayPlaying: false });
            return;
        }

        // Calculate delta time (in real milliseconds)
        const deltaMs = timestamp - this.lastTickTimestamp;
        this.lastTickTimestamp = timestamp;

        // Advance replay time (real ms * speed multiplier)
        const newTime = replayCurrentTime + (deltaMs * replaySpeed);

        this.setState({
            replayCurrentTime: Math.min(newTime, replayEndTime),
        });

        // Continue loop
        this.replayFrameId = requestAnimationFrame(this.replayTick);
    }

    handleReplayPlayPause = () => {
        const { replayPlaying } = this.state;
        if (replayPlaying) {
            this.stopReplayLoop();
            this.setState({ replayPlaying: false });
        } else {
            this.setState({ replayPlaying: true }, () => {
                this.startReplayLoop();
            });
        }
    }

    handleReplaySeek = (newTime) => {
        this.setState({ replayCurrentTime: newTime });
    }

    handleReplaySpeedChange = (speed) => {
        this.setState({ replaySpeed: speed });
    }

    computeReplayPositions = (trackers, currentTime) => {
        if (!trackers || currentTime == null) return [];

        // Get the elevation profile's max distance for scaling
        const { elevationProfile } = this.state;
        const profileMaxDist = elevationProfile && elevationProfile.length > 0
            ? elevationProfile[elevationProfile.length - 1].distance
            : 0;

        return trackers.map((tracker, idx) => {
            if (!tracker.points || tracker.points.length === 0) return null;

            let position;
            if (this.state.showRealPointsOnly) {
                const idx = findPointIndexBefore(tracker.points, currentTime);
                if (idx < 0) return null;
                const realPoint = tracker.points[idx];
                const prevPoint = idx > 0 ? tracker.points[idx - 1] : realPoint;
                position = {
                    lat: realPoint[0],
                    lng: realPoint[1],
                    speed: realPoint[3] || 0,
                    bearing: calculateBearing(prevPoint[0], prevPoint[1], realPoint[0], realPoint[1]),
                    battery: realPoint[4],
                    timestamp: new Date(realPoint[2]).getTime(),
                };
            } else {
                position = getPositionAtTime(tracker.points, currentTime);
            }
            if (!position) return null;

            // Get trail points up to current time
            const trailPoints = getPointsUpToTime(tracker.points, currentTime, 100);

            // Calculate cumulative distance traveled up to current time
            // Use backend's totalDistanceOnTrack if available, otherwise use profile max distance
            const originalTotalDistance = tracker.totalDistanceOnTrack || profileMaxDist;
            const totalDistanceOnTrack = calculateDistanceAtTime(
                tracker.points,
                currentTime,
                position,
                originalTotalDistance
            );

            return {
                ...tracker,
                lat: position.lat,
                lng: position.lng,
                bearing: position.bearing,
                avgSpeed: position.speed * 3.6, // Convert m/s to km/h if needed
                timestamp: currentTime,
                replayPoints: trailPoints,
                totalDistanceOnTrack, // Distance in meters for elevation profile positioning
                // Recalculate the last point for marker positioning
                point: [position.lat, position.lng, currentTime, position.speed, position.battery],
            };
        }).filter(t => t !== null);
    }

    // createMap = () => {
    //     // Create map
    //     // this.map = L.map('map').setView([51.505, -0.09], 13);

    //     // Add OpenStreetMap tiles
    //     // L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    //     //     maxZoom: 19,
    //     //     attribution: '© OpenStreetMap contributors'
    //     // }).addTo(this.map);
    // }

    // setMap = map => {
    //     this.map = map;
    //     console.log('setMap', { map })
    // }

    setMapRef = map => {
        this.mapRef = map;
        if (map) {
            map.on('click', this.handleMapClick);
        }
        // console.log('setMapRef', { map })
    }

    toggleRealPosition = () => {
        this.setState({
            realPosition: !this.state.realPosition,
        }, () => {
            // this.fitFoundMarkersToBounds();
        });
    }

    searchInput = (e) => {
        const search = e.target.value;
        this.setState({ search });
    }

    doSearch(search, markers = []) {
        const { realPosition } = this.state;
        const foundMarkers = markers.filter(marker => {
            const markerInfo = marker[2];
            // Skip GPS devices without a raceClassContestant mapping
            if (!markerInfo.contestant || !markerInfo.contestant.id) return false;
            const contestant = markerInfo.contestant;
            const searchStr = `${contestant.bib} ${contestant.firstname} ${contestant.lastname}`.toLowerCase();
            return searchStr.includes(search.toLowerCase());
        });
        return foundMarkers;
    }

    filterPointsBySearch(search, points) {
        const searchLower = search.toLowerCase();
        return points.filter(p => {
            if (!p.contestant || !p.contestant.id) return false;
            const c = p.contestant;
            return `${c.bib} ${c.firstname} ${c.lastname}`.toLowerCase().includes(searchLower);
        });
    }

    fitFoundMarkersToBounds = () => {
        const { gpsPointStore } = this.props.stores;
        const { markers = [] } = gpsPointStore;

        const { realPosition, foundMarkers = markers } = this.state;
        // Filter to only markers with valid contestant mapping
        const validMarkers = foundMarkers.filter(marker => {
            const markerInfo = marker[2];
            return markerInfo.contestant && markerInfo.contestant.id;
        });
        if (validMarkers.length > 0) {
            let markerBounds = L.latLngBounds([]);
            validMarkers.forEach(marker => {
                const markerInfo = marker[2];
                const contestant = markerInfo.contestant;
                const pt = realPosition ? [markerInfo.point[0], markerInfo.point[1]] : [marker[0], marker[1]];
                markerBounds.extend(pt);
            });
            this.mapRef.fitBounds(markerBounds, { padding: [100, 140] });
        }
    }

    zoomToAllMushers = () => {
        const { gpsPointStore } = this.props.stores;
        const { bounds } = gpsPointStore;

        if (!this.mapRef || !bounds || bounds.length === 0) return;

        this.mapRef.fitBounds(bounds, { padding: [100, 140] });
    }

    clearSearch = () => {
        // Reset search
        this.setState({
            search: '',
        });
    }

    handleKeyDown = e => {
        if (e.keyCode === 27) {
            // Esc key pressed
            e.preventDefault();
            e.stopPropagation();
            // console.log('Esc key pressed');
            this.clearSearch();
        }
    }

    changeReportDate = async e => {
        const { value: cutoffDate } = e.target;
        const { stopTimer = () => {} } = this.props;
        stopTimer();
        this.stopReloadTimer();
        this.setState({ cutoffDate }, () => {
            this.loadGpsPoints();
        });
    }

    clearCutoffDate = e => {
        this.setState({ cutoffDate: null }, () => {
            this.loadGpsPoints();
            // this.startReloadTimer();
        });
    }

    handleMapClick = (e) => {
        // console.log('handleMapClick', e);
        // Don't set position if clicking on a marker or other interactive element
        const target = e.originalEvent?.target;
        if (target) {
            // Check if click was on a marker element (has leaflet-marker-icon class or is inside one)
            const isMarkerClick = target.closest('.leaflet-marker-icon') ||
                                  target.closest('.leaflet-popup') ||
                                  target.closest('.waypoint-marker-icon') ||
                                  target.closest('.checkpoint-marker-icon') ||
                                  target.closest('.broadcast-channel-marker-icon');
            if (isMarkerClick) {
                return; // Don't create temp marker when clicking on existing markers
            }
        }
        this.setState({ currentPos: e.latlng });
    }

    changeMapType = e => {
        const { value: mapType } = e.target;
        const { appState } = this.props.stores;
        appState.setMapType(mapType);
    }

    viewRaceClassContestant = (e, id, markerInfo) => {
        const {
            view,
            race,
            raceClass,
            raceClassContestants,
        } = this.props;
        if (view) {
            return view(e);
        }
        const { appState, raceStore } = this.props.stores;
        const { isRaceAdmin } = raceStore;
        const { drawerLevel = 1 } = this.props;
        const { drawerHeightMedium } = appState;

        // Strip the large points array - only pass metadata for performance
        const { points, ...markerInfoWithoutPoints } = markerInfo || {};
        const drawerMarkerInfo = markerInfo ? {
            ...markerInfoWithoutPoints,
            count: markerInfo.count || (points ? points.length : 0),
            hasPoints: !!(points && points.length > 0),
        } : {};

        appState.openDrawer('viewRaceClassContestant', {
            id,
            // title,
            height: drawerHeightMedium,
            editFunction: this.editMediaViaView,
            race,
            raceClass,
            raceClassContestants,
            showResults: true,
            markerInfo: drawerMarkerInfo,
            isRaceAdmin,
            callback: () => {
                appState.toggleDrawer();
                // this.loadAll();
            },
        }, drawerLevel + 1);
    }

    adminAddPosition = (e, pos) => {
        // console.log({ e, pos });
        const { appState } = this.props.stores;
        const { drawerLevel = 1 } = this.props;
        const { drawerHeightMedium } = appState;
        // console.log('viewPart', { id });
        appState.openDrawer('adminViewGpsPosition', {
            pos,
            // title,
            height: drawerHeightMedium,
            callback: () => {
                appState.toggleDrawer();
                // this.loadAll();
            },
        }, drawerLevel + 1);
    }

    toggleCurrentPosition = () => {
        const { yourPosition } = this.state;
        this.setState({
            yourPosition: !yourPosition,
        }, () => {
            this.loadCurrentPosition();
        });
    }

    loadCurrentPosition = () => {
        const { yourPosition } = this.state;
        if (yourPosition) {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    position => {
                        const { latitude, longitude } = position.coords;
                        this.setState({
                            yourPoint: [latitude, longitude],
                        });
                        // Set timer
                        this.currentPositionTimer = setTimeout(() => {
                            this.loadCurrentPosition();
                        }, 1000 * 30);

                        // console.log({ latitude, longitude });
                    },
                    (error) => {
                        console.error('Error getting location', error);
                        // Handle location error (e.g., user denied location access)
                    }
                );
            } else {
                console.error('Geolocation is not supported by this browser.');
            }
        } else {
            clearTimeout(this.currentPositionTimer);
            this.setState({
                yourPoint: null,
            });
        }
    }

    setRaceClass = async (e) => {
        const { callback = () => {} } = this.props;
        const { id, skip = 0 } = e.target.closest('button').dataset;
        const idInt = parseInt(id, 10);
        const skipInt = parseInt(skip, 10);
        const { replayMode } = this.state;
        const { raceClassStore } = this.props.stores;

        // If in replay mode, stop replay and load new class for replay
        if (replayMode) {
            this.stopReplayLoop();
            this.setState({
                skipRaceClass: skipInt,
                rcId: idInt,
                replayPlaying: false,
                replayCurrentTime: null,
                replayStartTime: null,
                replayEndTime: null,
                replayTrackers: [],
            }, async () => {
                await this.loadRaceClassContestants();
                // Recalculate elevation profile with new race class files
                const { raceClass: newRaceClass } = raceClassStore;
                if (newRaceClass?.files) {
                    this.calculateRouteElevationProfile(newRaceClass.files);
                }
                await this.loadFullGpsData();
            });
        } else {
            this.setState({ skipRaceClass: skipInt, rcId: idInt }, async () => {
                await this.loadRaceClassContestants();
                // Recalculate elevation profile with new race class files
                const { raceClass: newRaceClass } = raceClassStore;
                if (newRaceClass?.files) {
                    this.calculateRouteElevationProfile(newRaceClass.files);
                }
                this.loadAll({ ...this.props, raceClassId: idInt, skipRaceClass: skip});
            });
        }
    }

    toggleElevationProfile = () => {
        this.setState({ showElevationProfile: !this.state.showElevationProfile });
    }

    toggleMobileMenu = () => {
        this.setState({ mobileMenuOpen: !this.state.mobileMenuOpen });
    }

    setRaceClassFromSelect = async (e) => {
        const value = e.target.value;
        const { replayMode } = this.state;
        const { raceClassStore } = this.props.stores;
        const { raceClass } = this.props;

        // Handle "show_all" option
        const isShowAll = value === 'show_all';
        const idInt = isShowAll ? raceClass.id : parseInt(value, 10);
        const skipInt = isShowAll ? 1 : 0;

        if (replayMode) {
            this.stopReplayLoop();
            this.setState({
                skipRaceClass: skipInt,
                rcId: idInt,
                replayPlaying: false,
                replayCurrentTime: null,
                replayStartTime: null,
                replayEndTime: null,
                replayTrackers: [],
            }, async () => {
                await this.loadRaceClassContestants();
                const { raceClass: newRaceClass } = raceClassStore;
                if (newRaceClass?.files) {
                    this.calculateRouteElevationProfile(newRaceClass.files);
                }
                await this.loadFullGpsData();
            });
        } else {
            this.setState({ skipRaceClass: skipInt, rcId: idInt }, async () => {
                await this.loadRaceClassContestants();
                const { raceClass: newRaceClass } = raceClassStore;
                if (newRaceClass?.files) {
                    this.calculateRouteElevationProfile(newRaceClass.files);
                }
                this.loadAll({ ...this.props, raceClassId: idInt, skipRaceClass: skipInt });
            });
        }
    }

    handleElevationPointClick = (point) => {
        // When user clicks on elevation profile, pan map to that location
        if (point && point.lat && point.lng && this.mapRef) {
            this.mapRef.setView([point.lat, point.lng], this.mapRef.getZoom());
        }
    }

    handleElevationHover = (point) => {
        // When user hovers on elevation profile, show marker on map
        this.setState({ elevationHoverPoint: point });
    }

    showRaceResults = e => {
        // Toggle results drawer
        const { appState } = this.props.stores;
        const {
            drawerLevel = 1,
            race,
            raceClass,
            raceClasses,
        } = this.props;
        const { drawerHeightMediumLarge } = appState;
        appState.openDrawer('viewRaceResults', {
            height: drawerHeightMediumLarge,
            raceClassId: raceClass.id,
            raceId: race.id,
            drawerLevel: drawerLevel + 1,
            callback: () => {
                appState.toggleDrawer();
                // this.loadAll();
            },
        }, drawerLevel + 1);
    }

    // Session tracking methods for analytics
    sendViewingSession = async (isFinal = false) => {
        const { race } = this.props;
        if (!race || !race.id || !this.viewStartTime) {
            return;
        }

        const endTime = Date.now();
        const duration = endTime - this.viewStartTime;

        // Only send if duration is at least 1 second
        if (duration < 1000) {
            return;
        }

        const trackingData = [{
            type: 'raceTracking',
            elementId: race.id,
            time: endTime,
            startTime: this.viewStartTime,
            endTime,
            duration,
            sessionId: this.viewSessionId,
        }];

        try {
            await util.fetchApi('/api/scrollview/', { method: 'POST' }, { data: trackingData });
        } catch (err) {
            // Silently fail - don't disrupt user experience
        }

        // If not final, reset start time for next interval
        if (!isFinal) {
            this.viewStartTime = endTime;
        }
    }

    startViewingSession = () => {
        // Generate unique session ID
        this.viewSessionId = `track_${Date.now()}_${Math.floor(Math.random() * 10000)}`;
        this.viewStartTime = Date.now();

        // Send heartbeat every 30 seconds to capture ongoing viewing
        this.viewingHeartbeatTimer = setInterval(() => {
            this.sendViewingSession(false);
        }, 30 * 1000);

        // Handle page visibility changes (user switches tabs)
        this.handleVisibilityChange = () => {
            if (document.hidden) {
                // User left the tab - send current session data
                this.sendViewingSession(false);
            } else {
                // User came back - reset start time
                this.viewStartTime = Date.now();
            }
        };
        document.addEventListener('visibilitychange', this.handleVisibilityChange);

        // Handle page unload (closing tab/window)
        this.handleBeforeUnload = () => {
            this.sendViewingSession(true);
        };
        window.addEventListener('beforeunload', this.handleBeforeUnload);
    }

    stopViewingSession = () => {
        if (this.viewingHeartbeatTimer) {
            clearInterval(this.viewingHeartbeatTimer);
            this.viewingHeartbeatTimer = null;
        }
        // Remove event listeners
        if (this.handleVisibilityChange) {
            document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        }
        if (this.handleBeforeUnload) {
            window.removeEventListener('beforeunload', this.handleBeforeUnload);
        }
        // Send final session data
        this.sendViewingSession(true);
    }

    async componentDidMount() {
        // Register global handler for waypoint clicks (needed for divIcon onclick)
        window.__editWaypoint = this.editWaypoint;

        // Set up 15-minute weather refresh interval
        // (Initial fetch happens in loadAll after files are loaded)
        this.weatherRefreshInterval = setInterval(() => {
            this.fetchTrackWeather();
        }, 15 * 60 * 1000); // 15 minutes

        // Set up Firebase Firestore listener for live GPS updates
        this.setupFirebaseGpsListener();

        await this.loadAll();
        // Also fetch from liverace.no on initial load
        this.fetchLiveraceData();
        // Start tracking viewing session for analytics
        this.startViewingSession();
    }

    componentWillUnmount() {
        // Clean up global handler
        delete window.__editWaypoint;

        // Clean up weather refresh interval
        if (this.weatherRefreshInterval) {
            clearInterval(this.weatherRefreshInterval);
            this.weatherRefreshInterval = null;
        }

        // Clean up weather fetch debounce timer
        if (this.weatherFetchDebounceTimer) {
            clearTimeout(this.weatherFetchDebounceTimer);
            this.weatherFetchDebounceTimer = null;
        }

        // Clean up Firebase GPS listener
        if (this.firebaseUnsubscribe) {
            this.firebaseUnsubscribe();
            this.firebaseUnsubscribe = null;
        }
        if (this.firebaseGpsDebounceTimer) {
            clearTimeout(this.firebaseGpsDebounceTimer);
            this.firebaseGpsDebounceTimer = null;
        }

        this.stopReloadTimer();
        this.stopReplayLoop();
        // Stop tracking viewing session
        this.stopViewingSession();
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.race !== this.props.race || nextProps.raceClass !== this.props.raceClass) {
            this.loadAll(nextProps);
        }
    }

    // Geofence edit methods
    toggleGeofenceEdit = () => {
        this.setState(prevState => ({
            editGeofences: !prevState.editGeofences,
            showGeofences: !prevState.editGeofences ? true : prevState.showGeofences,
        }));
    }

    toggleShowGeofences = () => {
        this.setState(prevState => ({
            showGeofences: !prevState.showGeofences,
        }));
    }

    toggleShowRealPointsOnly = () => {
        this.setState(prevState => ({
            showRealPointsOnly: !prevState.showRealPointsOnly,
        }));
    }

    loadAllSatPoints = async () => {
        const { race, raceClass } = this.props;
        const { skipRaceClass, rcId } = this.state;
        if (!race?.id) return;

        this.setState({ loadingAllSatPoints: true });
        const raceClassId = skipRaceClass ? '' : (rcId || raceClass?.id || '');

        try {
            const response = await fetch(`https://www.liverace.no/api/gpspoints/group?race=${race.id}&raceClass=${raceClassId}&allPoints=true`, {
                credentials: 'include',
                method: 'GET',
                mode: 'cors',
                headers: { 'Accept': 'application/json' },
            });

            if (response.ok) {
                const data = await response.json();
                const trackers = data?.data || [];
                const satPoints = [];
                trackers.forEach(tracker => {
                    const points = tracker.points || [];
                    points.forEach(p => {
                        if (p[6] === 'sat') {
                            satPoints.push({
                                lat: p[0],
                                lng: p[1],
                                timestamp: p[2],
                                battery: p[3],
                                elevation: p[5],
                                trackerId: tracker.trackerId,
                                contestant: tracker.contestant,
                            });
                        }
                    });
                });
                this.setState({ allSatPoints: satPoints, allSatPointsLoaded: true, loadingAllSatPoints: false });
            } else {
                this.setState({ loadingAllSatPoints: false });
            }
        } catch (err) {
            console.log('Failed to load all sat points:', err.message);
            this.setState({ loadingAllSatPoints: false });
        }
    }

    toggleShowAllSatPoints = () => {
        this.setState(prevState => {
            const newShow = !prevState.showAllSatPoints;
            if (newShow && !prevState.allSatPointsLoaded) {
                this.loadAllSatPoints();
            }
            return { showAllSatPoints: newShow };
        });
    }

    loadDebugGpsPoints = async () => {
        const { race, raceClass } = this.props;
        const { skipRaceClass, rcId } = this.state;
        if (!race?.id) return;

        this.setState({ loadingDebugGpsPoints: true });
        const raceClassId = skipRaceClass ? '' : (rcId || raceClass?.id || '');

        try {
            const response = await fetch(`https://www.liverace.no/api/gpspoints/group?race=${race.id}&raceClass=${raceClassId}&allPoints=true`, {
                credentials: 'include',
                method: 'GET',
                mode: 'cors',
                headers: { 'Accept': 'application/json' },
            });

            if (response.ok) {
                const data = await response.json();
                const trackers = data?.data || [];
                const allPoints = [];
                trackers.forEach(tracker => {
                    const points = tracker.points || [];
                    points.forEach(p => {
                        allPoints.push({
                            lat: p[0],
                            lng: p[1],
                            timestamp: p[2],
                            battery: p[3],
                            speed: p[4],
                            elevation: p[5],
                            type: p[6] || 'gsm',
                            trackerId: tracker.trackerId,
                            contestant: tracker.contestant,
                        });
                    });
                });
                this.setState({ debugGpsPoints: allPoints, debugGpsPointsLoaded: true, loadingDebugGpsPoints: false });
            } else {
                this.setState({ loadingDebugGpsPoints: false });
            }
        } catch (err) {
            console.log('Failed to load debug GPS points:', err.message);
            this.setState({ loadingDebugGpsPoints: false });
        }
    }

    toggleShowDebugGpsPoints = () => {
        this.setState(prevState => {
            const newShow = !prevState.showDebugGpsPoints;
            if (newShow && !prevState.debugGpsPointsLoaded) {
                this.loadDebugGpsPoints();
            }
            return { showDebugGpsPoints: newShow };
        });
    }

    toggleShowCoverage = () => {
        this.setState(prevState => ({ showCoverage: !prevState.showCoverage }));
    }

    openGeofenceEditor =(checkpointMd5, arrivalRadius, departureRadius) => {
        this.setState({
            editingCheckpointMd5: checkpointMd5,
            pendingGeofenceArrival: arrivalRadius || 500,
            pendingGeofenceDeparture: departureRadius || 700,
        });
    }

    closeGeofenceEditor = () => {
        this.setState({
            editingCheckpointMd5: null,
            pendingGeofenceArrival: null,
            pendingGeofenceDeparture: null,
        });
    }

    updatePendingGeofence = (field, value) => {
        this.setState({ [field]: parseInt(value) || 0 });
    }

    saveGeofenceSettings = async () => {
        const { editingCheckpointMd5, pendingGeofenceArrival, pendingGeofenceDeparture } = this.state;
        const { raceClass } = this.props;

        if (!editingCheckpointMd5 || !raceClass) return;

        try {
            // Save arrival radius
            await localUtil.fetchApi(`/api/raceclasses/${raceClass.id}/patch-admin`, { method: 'POST' }, {
                editCheckpointsByMd5: editingCheckpointMd5,
                editCheckpointsKey: 'geofenceArrivalRadius',
                editCheckpointsValue: pendingGeofenceArrival,
            });

            // Save departure radius
            await localUtil.fetchApi(`/api/raceclasses/${raceClass.id}/patch-admin`, { method: 'POST' }, {
                editCheckpointsByMd5: editingCheckpointMd5,
                editCheckpointsKey: 'geofenceDepartureRadius',
                editCheckpointsValue: pendingGeofenceDeparture,
            });

            // Update local state
            const cp = raceClass.checkpoints.find(c => c.md5 === editingCheckpointMd5);
            if (cp) {
                cp.geofenceArrivalRadius = pendingGeofenceArrival;
                cp.geofenceDepartureRadius = pendingGeofenceDeparture;
            }

            this.closeGeofenceEditor();
        } catch (err) {
            console.error('Failed to save geofence settings:', err);
        }
    }

    render() {
        const { realPosition, yourPosition, yourPoint, currentPos, search = '', cutoffDate, replayMode, replayPlaying, replayCurrentTime, replaySpeed, replayStartTime, replayEndTime, replayTrackers, isLoadingReplay, noReplayData, showElevationProfile, elevationStats, elevationProfile, elevationHoverPoint, autoUpdate, autoUpdateCountdown, editGeofences, showGeofences, editingCheckpointMd5, pendingGeofenceArrival, pendingGeofenceDeparture, trackWeather, showRealPointsOnly, geofenceSoundEnabled, showAllSatPoints, allSatPoints, loadingAllSatPoints, showDebugGpsPoints, debugGpsPoints, loadingDebugGpsPoints, showCoverage } = this.state;
        const { skipRaceClass, rcId } = this.state;
        const { appState, userStore, raceStore, raceClassStore, gpsPointStore, broadcastChannelStore } = this.props.stores;

        const { user, isAdmin } = userStore;
        const { isRaceAdmin, isMediaWorker, isTimeTracker, isRaceLeader, isTdLeader } = raceStore;
        const broadcastChannelsWithPosition = (isAdmin || isRaceAdmin) ? (broadcastChannelStore?.broadcastChannels || []).filter(ch => ch.lat && ch.lng) : [];
        const showAllButton = isAdmin || isRaceAdmin || isMediaWorker || isTimeTracker || isRaceLeader || isTdLeader;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');

        const { titleText } = this.props;
        const {
            race,
            raceClass,
            raceClasses,
            raceClassContestants,
            isFixed = true,
            inputMarkers = [],
        } = this.props;

        const { isDevelopment, path, apiServer, mapType = 'topograatone' } = appState;
        const mapTypeFixed = race.mapType;
        let mapTypeFinal = mapType;
        if (mapTypeFixed) {
            mapTypeFinal = mapTypeFixed;
        }

        const { gpsPoints, lastPoint, polyline, trackers: liveTrackers = [], markers: liveMarkers = [], lines = [], weather = {}, totalTrackLength = 0 } = gpsPointStore;
        const { files } = raceClass;
        const finalRaceClasses = raceClasses?.filter(rc => !rc.title.includes('⚠️'));

        // Use replay positions if in replay mode, otherwise live data
        let trackers = liveTrackers;
        let markers = liveMarkers;

        // During replay loading, show no markers to prevent render errors
        if (isLoadingReplay) {
            trackers = [];
            markers = [];
        } else if (replayMode) {
            // In replay mode, compute positions from replay trackers
            if (replayTrackers.length > 0 && replayCurrentTime != null) {
                trackers = this.computeReplayPositions(replayTrackers, replayCurrentTime);
                // For replay, we need to regenerate markers from computed positions with full opacity
                markers = trackers.map((tracker, idx) => {
                    if (!tracker || !tracker.lat || !tracker.lng) return null;
                    // Skip GPS devices without a raceClassContestant mapping
                    if (!tracker.contestant || !tracker.contestant.id) return null;
                    // Create replay marker with full opacity (not time-based)
                    const icon = createReplayMarkerIcon(tracker, idx);
                    const markerInfo = {
                        ...tracker,
                        icon,
                        point: [tracker.lat, tracker.lng],
                    };
                    // Format must match live markers: [lat, lng, markerInfo]
                    return [tracker.lat, tracker.lng, markerInfo];
                }).filter(m => m !== null);
            } else {
                // Still loading replay data - show empty markers
                trackers = [];
                markers = [];
            }
        }

        const colorGenerator = colorCycler();

        return (<>
            <div class='w-100 h-100 d-flex flex-column skipRefresh position-relative'
                // style={`
                //     height: calc(100% - env(safe-area-inset-top) - env(safe-area-inset-bottom));
                // `}
            >
                <div
                    class={`w-100 h-100 ${isFixed ? 'position-fixed' : 'position-relative'}`}
                    // style={`
                    //     height: calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom));
                    // `}
                    // onScroll={localUtil.captureAllEvents}
                    // onTouchMove={localUtil.captureAllEvents}
                    // onMouseDrag={localUtil.captureAllEvents}
                >
                    <MapContainer
                        center={[lastPoint.lat, lastPoint.lng]}
                        zoom={12}
                        scrollWheelZoom={true}
                        style={{ height: '100%', width: '100%' }}
                        ref={this.setMapRef}
                    >
                        <ResizeMap />
                        <ZoomTracker onZoomChange={(zoom) => this.setState({ currentZoom: zoom })} />
                        {/* <ChangeView center={[lastPoint.lat, lastPoint.lng]} zoom={11} bounds={bounds} /> */}
                        {mapTypeFinal === 'openstreetmap' ? <>
                            <TileLayer
                                key={`tilelayer-${mapTypeFinal}`}
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                        </> : <>
                            <TileLayer
                                key={`tilelayer-${mapTypeFinal}`}
                                attribution='&copy; <a href="https://statkart.no">Kartverket</a>'
                                url={`https://cache.kartverket.no/v1/wmts/1.0.0/${mapTypeFinal}/default/webmercator/{z}/{y}/{x}.png`}
                                subdomains={['', '2', '3']}
                            />
                        </>}
                        {/* // url={`https://opencache{s}.statkart.no/gatekeeper/gk/gk.open_gmaps?layers=${mapTypeFinal}&zoom={z}&x={x}&y={y}`} */}

                        {/* https://opencache3.statkart.no/gatekeeper/gk/gk.open_gmaps?layers=topo4graatone&zoom=11&x=1184&y=456 */}

                        <ScaleControl position='topleft' imperial={false} />

                        {showCoverage && <CoverageOverlay
                            files={files}
                            trackers={replayMode ? replayTrackers : liveTrackers}
                        />}

                        {files && files.length > 0 && files.map(file => {
                            if (file.geoJSON) {
                                return (<>
                                    <GeoJSON
                                        key={`stroke-${file.md5}`}
                                        data={file.geoJSON}
                                        style={{
                                            weight: 10,                      // Stroke weight in pixels
                                            opacity: 0.5,                    // Stroke opacity
                                            color: "rgb(255, 255, 255)",           // Stroke color
                                        }}
                                        pointToLayer={(feature, latlng) => {
                                            return L.marker(latlng, { icon: iconCircle });
                                        }}
                                    />
                                    <GeoJSON
                                        key={`fill-${file.md5}`}
                                        data={file.geoJSON}
                                        style={(feature, idx) => {
                                            // console.log({ feature, idx });
                                            // console.log('name', feature?.properties?.name);

                                            switch (feature.geometry.type) {
                                                case 'Polygon':
                                                case 'MultiPolygon':
                                                    // return {
                                                    //     weight: 5,                       // Stroke weight in pixels
                                                    //     opacity: 0.6,                    // Stroke opacity
                                                    //     color: "rgb(54, 133, 252)",      // Stroke color
                                                    // };
                                                case 'LineString':
                                                case 'MultiLineString':
                                                    // return {
                                                    //     weight: 7,                       // Stroke weight in pixels
                                                    //     opacity: 0.6,                    // Stroke opacity
                                                    //     color: "rgb(54, 133, 252)",      // Stroke color
                                                    // };     // Styles for lines
                                                // Add more cases for other geometry types if needed
                                                default:
                                                    return {
                                                        weight: 6,                       // Stroke weight in pixels
                                                        opacity: 0.6,                    // Stroke opacity
                                                        color: colorGenerator.next().value, //"rgb(54, 133, 252)",      // Stroke color
                                                    }; // Default style
                                            }
                                        }}
                                        // onEachFeature={(feature, layer) => {
                                        //     // console.log({ feature, layer });
                                        //     console.log(feature.properties);
                                        // }}
                                        // filter={(feature) => {
                                        //     // console.log({ feature });
                                        //     return true;
                                        // }}
                                        pointToLayer={(feature, latlng) => {
                                            return L.marker(latlng, { icon: iconCircle });
                                        }}
                                    />
                                    {/* Direction arrows along the track with distance labels */}
                                    {getArrowPositions(file.geoJSON, getDistanceInterval(this.state.currentZoom)).map((arrow, arrowIdx) => {
                                        const distanceLabel = Math.round(arrow.distanceKm);
                                        const arrowIcon = new L.divIcon({
                                            className: '',
                                            iconAnchor: [7, 7],
                                            iconSize: [60, 14],
                                            html: `<div style="
                                                display: flex;
                                                align-items: center;
                                                gap: 3px;
                                                white-space: nowrap;
                                            ">
                                                <div style="
                                                    transform: rotate(${arrow.bearing}deg);
                                                    color: rgba(40, 100, 200, 0.95);
                                                    font-size: 14px;
                                                    text-shadow: 0 0 2px white, 0 0 2px white, 0 0 4px white;
                                                "><i class="fa-solid fa-chevron-up"></i></div>
                                                <div style="
                                                    font-size: 10px;
                                                    font-weight: bold;
                                                    color: #555;
                                                    text-shadow: 0 0 2px white, 0 0 2px white, 0 0 3px white;
                                                    line-height: 1;
                                                ">${distanceLabel}km</div>
                                            </div>`,
                                        });
                                        return (
                                            <Marker
                                                key={`arrow-${file.md5}-${arrowIdx}`}
                                                position={[arrow.lat, arrow.lng]}
                                                icon={arrowIcon}
                                                interactive={false}
                                            />
                                        );
                                    })}
                                </>);
                            }
                        })}

                        {/* Weather markers along the track at 20km intervals */}
                        {trackWeather && trackWeather.length > 0 && trackWeather.map((weatherPoint, weatherIdx) => {
                            const { lat, lng, distanceKm, weather: weatherData } = weatherPoint;
                            const currentTemp = weatherData?.instant?.details?.air_temperature;
                            const weatherSymbol = weatherData?.next_1_hours?.summary?.symbol_code || weatherData?.next_6_hours?.summary?.symbol_code;
                            const windSpeed = weatherData?.instant?.details?.wind_speed;
                            const windDir = weatherData?.instant?.details?.wind_from_direction;
                            const weatherIcon = getWeatherIcon(weatherSymbol);
                            const windDirection = getWindDirection(windDir);

                            // Get wind arrow rotation (wind comes FROM this direction, so arrow points opposite)
                            const windArrowRotation = windDir != null ? (windDir + 180) % 360 : 0;

                            const weatherMarkerIcon = new L.divIcon({
                                className: '',
                                iconAnchor: [0, 35],
                                iconSize: [95, 24],
                                html: `<div style="
                                    display: flex;
                                    align-items: center;
                                    gap: 4px;
                                    background: rgba(255, 255, 255, 0.75);
                                    border-radius: 12px;
                                    padding: 2px 6px;
                                    font-size: 10px;
                                    border: 1px solid rgba(0, 0, 0, 0.12);
                                    white-space: nowrap;
                                ">
                                    ${weatherIcon ? `<i class="fa-solid ${weatherIcon}" style="color: #888;"></i>` : ''}
                                    <span style="color: ${currentTemp != null && currentTemp < 0 ? '#4499dd' : '#cc8800'}; font-weight: 500;">${currentTemp != null ? util.format(currentTemp, 0) + '°' : ''}</span>
                                    ${windSpeed != null ? `<span style="color: #999; font-size: 9px;"><i class="fa-solid fa-arrow-up" style="transform: rotate(${windArrowRotation}deg); font-size: 8px;"></i> ${util.format(windSpeed, 0)} m/s</span>` : ''}
                                </div>`,
                            });

                            return (
                                <Marker
                                    key={`weather-${weatherIdx}`}
                                    position={[lat, lng]}
                                    icon={weatherMarkerIcon}
                                    interactive={false}
                                />
                            );
                        })}

                        {raceClass && raceClass.checkpoints && <>
                            {raceClass.checkpoints.map(cp => {
                                const { lat, lng, rotateDeg, md5 } = cp;
                                const weatherData = weather[md5];
                                let currentTemp;
                                let weatherSymbol;
                                let windSpeed;
                                let windDir;
                                if (weatherData) {
                                    const currentWeather = weatherData[0];
                                    currentTemp = currentWeather?.instant?.details?.air_temperature;
                                    weatherSymbol = currentWeather?.next_1_hours?.summary?.symbol_code;
                                    windSpeed = currentWeather?.instant?.details?.wind_speed;
                                    windDir = currentWeather?.instant?.details?.wind_from_direction;
                                }
                                const weatherIcon = getWeatherIcon(weatherSymbol);
                                const windDirection = getWindDirection(windDir);

                                if (!cp || !cp.lat || !cp.lng) {
                                    return '';
                                }

                                const elevation = cp.elevation || cp.altitude;
                                const iconMrk = new L.divIcon({
                                    className: 'leaflet-marker-icon checkpoint-marker-icon',
                                    iconAnchor: [7,14],
                                    iconSize: [300, elevation ? 42 : 30],
                                    html: `
                                    <div
                                        class='d-flex flex-row justify-content-start align-items-center'
                                        style='transform-origin: left bottom; ${rotateDeg ? `transform: rotate(${rotateDeg}deg);` : ''} pointer-events: none;'
                                    >
                                        <div class='rounded-circle me-1' style='width: 14px; height: 14px; background-color: #00aa00; border: 2px solid #ffffff; box-shadow: 0 0 3px rgba(0,0,0,0.5);'></div>
                                        <div class='d-flex flex-column'>
                                            <div class='rounded-pill px-2 py-0' style='font-size: 12px; font-weight: 600; background-color: rgba(255,255,255,0.92); color: #333; pointer-events: none; box-shadow: 0 1px 3px rgba(0,0,0,0.3); border: 1px solid rgba(0,0,0,0.15);'>
                                                ${cp.name}${currentTemp != null ? ` <span style='color: ${currentTemp < 0 ? '#2288cc' : '#dd8500'};'>${weatherIcon ? `<i class='fa-solid ${weatherIcon}'></i> ` : ''}${util.format(currentTemp, 1)}°</span>` : ''}${windSpeed != null ? ` <span style='color: #666; font-size: 9px;'><i class='fa-solid fa-wind'></i> ${util.format(windSpeed, 1)} ${windDirection}</span>` : ''}
                                            </div>
                                            ${elevation ? `<div style='font-size: 9px; color: #555; white-space: nowrap; padding-left: 2px; font-weight: 500;'><i class='fa-solid fa-mountain' style='font-size: 7px;'></i> ${Math.round(elevation)} m</div>` : ''}
                                        </div>
                                    </div>`,
                                });
                                const arrivalRadius = cp.geofenceArrivalRadius || race?.checkpointArrivalFence || 0;
                                const departureRadius = cp.geofenceDepartureRadius || race?.checkpointDepartureFence || 0;
                                const hasGeofence = arrivalRadius > 0 && departureRadius > 0;
                                const isEditing = editingCheckpointMd5 === md5;

                                return (<>
                                    <Marker position={[lat, lng]} icon={iconMrk} interactive={editGeofences}>
                                        {editGeofences && (
                                            <Popup>
                                                <div style='min-width: 200px;'>
                                                    <h6 class='mb-2'>{cp.name}</h6>
                                                    {!hasGeofence && (
                                                        <div class='alert alert-warning py-1 px-2 small mb-2'>
                                                            <i class='fa-solid fa-exclamation-triangle me-1'></i>
                                                            Auto-detection disabled (no radius set)
                                                        </div>
                                                    )}
                                                    <div class='mb-2'>
                                                        <label class='form-label small mb-0'>Arrival Radius (m)</label>
                                                        <input
                                                            type='number'
                                                            class='form-control form-control-sm'
                                                            value={isEditing ? pendingGeofenceArrival : (arrivalRadius || 500)}
                                                            onFocus={() => this.openGeofenceEditor(md5, arrivalRadius || 500, departureRadius || 700)}
                                                            onInput={(e) => this.updatePendingGeofence('pendingGeofenceArrival', e.target.value)}
                                                            min={50}
                                                            max={2000}
                                                            step={50}
                                                        />
                                                    </div>
                                                    <div class='mb-2'>
                                                        <label class='form-label small mb-0'>Departure Radius (m)</label>
                                                        <input
                                                            type='number'
                                                            class='form-control form-control-sm'
                                                            value={isEditing ? pendingGeofenceDeparture : (departureRadius || 700)}
                                                            onFocus={() => this.openGeofenceEditor(md5, arrivalRadius || 500, departureRadius || 700)}
                                                            onInput={(e) => this.updatePendingGeofence('pendingGeofenceDeparture', e.target.value)}
                                                            min={100}
                                                            max={3000}
                                                            step={50}
                                                        />
                                                    </div>
                                                    <button class='btn btn-primary btn-sm w-100' onClick={this.saveGeofenceSettings}>
                                                        Save
                                                    </button>
                                                </div>
                                            </Popup>
                                        )}
                                    </Marker>
                                    {(editGeofences || (showGeofences && hasGeofence)) && (
                                        <>
                                            <Circle
                                                center={[parseFloat(lat), parseFloat(lng)]}
                                                radius={isEditing ? pendingGeofenceArrival : (arrivalRadius || 500)}
                                                pathOptions={{
                                                    color: '#28a745',
                                                    fillColor: '#28a745',
                                                    fillOpacity: 0.1,
                                                    weight: 2,
                                                    dashArray: editGeofences ? '5, 5' : null
                                                }}
                                            />
                                            <Circle
                                                center={[parseFloat(lat), parseFloat(lng)]}
                                                radius={isEditing ? pendingGeofenceDeparture : (departureRadius || 700)}
                                                pathOptions={{
                                                    color: '#fd7e14',
                                                    fillColor: '#fd7e14',
                                                    fillOpacity: 0.05,
                                                    weight: 2,
                                                    dashArray: editGeofences ? '5, 5' : null
                                                }}
                                            />
                                            {cp.proximityAlertEnabled && cp.proximityAlertRadius > 0 && (
                                                <Circle
                                                    center={[parseFloat(lat), parseFloat(lng)]}
                                                    radius={cp.proximityAlertRadius}
                                                    pathOptions={{
                                                        color: '#0d6efd',
                                                        fillColor: '#0d6efd',
                                                        fillOpacity: 0.03,
                                                        weight: 1.5,
                                                        dashArray: '10, 6',
                                                    }}
                                                />
                                            )}
                                        </>
                                    )}
                                </>);
                            })}
                        </>}

                        {/* Broadcast channel markers (admin only) */}
                        {(isAdmin || isRaceAdmin) && broadcastChannelsWithPosition.length > 0 && <>
                            {broadcastChannelsWithPosition.map(ch => {
                                const typeIcon = ch.type === 'radio' ? 'fa-radio' : ch.type === 'stream' ? 'fa-video' : 'fa-tv';
                                const iconMrk = new L.divIcon({
                                    className: 'leaflet-marker-icon broadcast-channel-marker-icon',
                                    iconAnchor: [7, 14],
                                    iconSize: [250, 30],
                                    html: `
                                    <div
                                        class='d-flex flex-row justify-content-start align-items-center'
                                        style='transform-origin: left bottom; ${ch.rotateDeg ? `transform: rotate(${ch.rotateDeg}deg);` : ''} pointer-events: none;'
                                    >
                                        <div class='d-flex align-items-center justify-content-center rounded-circle me-1' style='width: 14px; height: 14px; background-color: #7c3aed; border: 2px solid #ffffff; box-shadow: 0 0 3px rgba(0,0,0,0.5);'>
                                            <i class='fa-solid fa-broadcast-tower text-white' style='font-size: 6px;'></i>
                                        </div>
                                        <div class='rounded-pill px-2 py-0' style='font-size: 12px; font-weight: 600; background-color: rgba(124,58,237,0.9); color: #fff; pointer-events: none; box-shadow: 0 1px 3px rgba(0,0,0,0.3); border: 1px solid rgba(124,58,237,0.5);'>
                                            <i class='fa-solid ${typeIcon}' style='font-size: 10px; margin-right: 3px;'></i>${ch.name || 'Channel'}
                                        </div>
                                    </div>`,
                                });
                                return (
                                    <Marker position={[ch.lat, ch.lng]} icon={iconMrk} interactive={true}>
                                        <Popup>
                                            <div style='min-width: 150px;'>
                                                <h6 class='mb-1'>
                                                    <i class={`fa-solid fa-broadcast-tower me-1`} />
                                                    {ch.name}
                                                </h6>
                                                <div class='small text-muted'>
                                                    <i class={`fa-solid ${typeIcon} me-1`} />
                                                    {ch.type === 'tv' ? 'TV' : ch.type === 'radio' ? 'Radio' : 'Stream'}
                                                </div>
                                                {ch.description && <div class='small mt-1'>{ch.description}</div>}
                                                <div class='small text-muted mt-1'>
                                                    {util.format(ch.lat, 5)}, {util.format(ch.lng, 5)}
                                                </div>
                                            </div>
                                        </Popup>
                                    </Marker>
                                );
                            })}
                        </>}

                        {/* Geofence legend */}
                        {(editGeofences || showGeofences) && (
                            <div
                                style='position: absolute; bottom: 20px; right: 10px; background: white; padding: 8px; border-radius: 4px; z-index: 1000; box-shadow: 0 2px 6px rgba(0,0,0,0.3);'
                            >
                                <div class='small'>
                                    <span style='display: inline-block; width: 12px; height: 12px; background: #28a745; border-radius: 50%; margin-right: 4px;'></span>
                                    Arrival fence (check-in)
                                </div>
                                <div class='small'>
                                    <span style='display: inline-block; width: 12px; height: 12px; background: #fd7e14; border-radius: 50%; margin-right: 4px;'></span>
                                    Departure fence (check-out)
                                </div>
                                <div class='small text-muted'>
                                    <i class='fa-solid fa-circle-xmark me-1'></i>
                                    No circle = auto-detection disabled
                                </div>
                            </div>
                        )}

                        {raceClass && raceClass.waypoints && <>
                            {raceClass.waypoints.map(wp => {
                                const { lat, lng, rotateDeg, icon, md5 } = wp;
                                const weatherData = weather[md5];
                                let currentTemp;
                                let weatherSymbol;
                                let windSpeed;
                                let windDir;
                                if (weatherData) {
                                    const currentWeather = weatherData[0];
                                    currentTemp = currentWeather?.instant?.details?.air_temperature;
                                    weatherSymbol = currentWeather?.next_1_hours?.summary?.symbol_code;
                                    windSpeed = currentWeather?.instant?.details?.wind_speed;
                                    windDir = currentWeather?.instant?.details?.wind_from_direction;
                                }
                                const weatherIcon = getWeatherIcon(weatherSymbol);
                                const windDirection = getWindDirection(windDir);

                                if (!wp || !wp.lat || !wp.lng) {
                                    return '';
                                }

                                const elevation = wp.elevation || wp.altitude;
                                const iconMrk = new L.divIcon({
                                    className: 'leaflet-marker-icon waypoint-marker-icon',
                                    iconAnchor: [2,7],
                                    iconSize: [240, elevation ? 28 : 18],
                                    html: `
                                    <div class='d-flex flex-row justify-content-start align-items-center' style='opacity: 0.8;'>
                                        <div class='d-flex align-items-center justify-content-center rounded-circle me-1' style='width: 10px; height: 10px; background-color: #8080ff; border: 1px solid #ffffff;'>
                                            ${icon ? `<i class='${icon} text-white' style='font-size: 6px;'></i>` : ''}
                                        </div>
                                        <div class='d-flex flex-column'>
                                            <div class='rounded-pill px-1' style='font-size: 9px; background-color: rgba(255,255,255,0.7); color: #555; white-space: nowrap;'>
                                                ${wp.name}${currentTemp != null ? ` <span style='color: ${currentTemp < 0 ? '#2288cc' : '#dd8500'};'>${weatherIcon ? `<i class='fa-solid ${weatherIcon}'></i> ` : ''}${util.format(currentTemp, 1)}°</span>` : ''}${windSpeed != null ? ` <span style='color: #888; font-size: 7px;'><i class='fa-solid fa-wind'></i> ${util.format(windSpeed, 1)} ${windDirection}</span>` : ''}
                                            </div>
                                            ${elevation ? `<div style='font-size: 7px; color: #888; white-space: nowrap; padding-left: 2px;'><i class='fa-solid fa-mountain' style='font-size: 5px;'></i> ${Math.round(elevation)} m</div>` : ''}
                                        </div>
                                    </div>`,
                                });
                                return (
                                    <Marker
                                        key={`raceclass-waypoint-${md5}`}
                                        position={[lat, lng]}
                                        icon={iconMrk}
                                    />
                                );
                            })}
                        </>}

                        {race && race.waypoints && <>
                            {race.waypoints.map(wp => {
                                const { lat, lng, rotateDeg, icon, md5 } = wp;
                                const weatherData = weather[md5];
                                let currentTemp;
                                let weatherSymbol;
                                let windSpeed;
                                let windDir;
                                if (weatherData) {
                                    const currentWeather = weatherData[0];
                                    currentTemp = currentWeather?.instant?.details?.air_temperature;
                                    weatherSymbol = currentWeather?.next_1_hours?.summary?.symbol_code;
                                    windSpeed = currentWeather?.instant?.details?.wind_speed;
                                    windDir = currentWeather?.instant?.details?.wind_from_direction;
                                }
                                const weatherIcon = getWeatherIcon(weatherSymbol);
                                const windDirection = getWindDirection(windDir);

                                if (!wp || !wp.lat || !wp.lng) {
                                    return '';
                                }

                                const elevation = wp.elevation || wp.altitude;
                                const iconMrk = new L.divIcon({
                                    className: 'leaflet-marker-icon waypoint-marker-icon',
                                    iconAnchor: [2,7],
                                    iconSize: [240, elevation ? 28 : 18],
                                    html: `
                                    <div class='d-flex flex-row justify-content-start align-items-center' style='opacity: 0.8;'>
                                        <div class='d-flex align-items-center justify-content-center rounded-circle me-1' style='width: 10px; height: 10px; background-color: #8080ff; border: 1px solid #ffffff;'>
                                            ${icon ? `<i class='${icon} text-white' style='font-size: 6px;'></i>` : ''}
                                        </div>
                                        <div class='d-flex flex-column'>
                                            <div class='rounded-pill px-1' style='font-size: 9px; background-color: rgba(255,255,255,0.7); color: #555; white-space: nowrap;'>
                                                ${wp.name}${currentTemp != null ? ` <span style='color: ${currentTemp < 0 ? '#2288cc' : '#dd8500'};'>${weatherIcon ? `<i class='fa-solid ${weatherIcon}'></i> ` : ''}${util.format(currentTemp, 1)}°</span>` : ''}${windSpeed != null ? ` <span style='color: #888; font-size: 7px;'><i class='fa-solid fa-wind'></i> ${util.format(windSpeed, 1)} ${windDirection}</span>` : ''}
                                            </div>
                                            ${elevation ? `<div style='font-size: 7px; color: #888; white-space: nowrap; padding-left: 2px;'><i class='fa-solid fa-mountain' style='font-size: 5px;'></i> ${Math.round(elevation)} m</div>` : ''}
                                        </div>
                                    </div>`,
                                });
                                return (
                                    <Marker
                                        key={`race-waypoint-${md5}`}
                                        position={[lat, lng]}
                                        icon={iconMrk}
                                    />
                                );
                            })}
                        </>}

                        {!realPosition && lines.map(line => {
                            // Skip lines with invalid coordinates
                            if (!line || typeof line[0] !== 'number' || typeof line[1] !== 'number' ||
                                typeof line[2] !== 'number' || typeof line[3] !== 'number' ||
                                !isFinite(line[0]) || !isFinite(line[1]) ||
                                !isFinite(line[2]) || !isFinite(line[3])) return null;
                            return (<>
                                <Polyline
                                    positions={[[line[0], line[1]], [line[2], line[3]]]}
                                    pathOptions={{
                                        color: 'black',
                                        weight: 1,
                                        dashArray: '4',
                                    }}
                                />
                            </>);
                        })}

                        {isAdmin && currentPos && <>
                            <Marker
                                position={currentPos}
                                draggable={true}
                                icon={iconMarker}
                                eventHandlers={{
                                    click: (e) => {
                                        this.adminAddPosition(e, currentPos);
                                    },
                                }}
                            >
                            </Marker>
                        </>}

                        {(search ? this.doSearch(search, markers) : markers).map((marker, idx) => {
                            if (!marker || !marker[2]) return null;
                            const markerInfo = marker[2];
                            // Skip markers without valid icons
                            if (!markerInfo.icon) return null;
                            // Skip GPS devices without a raceClassContestant mapping
                            if (!markerInfo.contestant || !markerInfo.contestant.id) return null;
                            const contestant = markerInfo.contestant;
                            // marker format is [lat, lng, markerInfo]
                            const pt = realPosition ? [markerInfo.point?.[0], markerInfo.point?.[1]] : [marker[0], marker[1]];
                            // Skip if coordinates are invalid (must be valid numbers, not null/undefined/NaN)
                            if (typeof pt[0] !== 'number' || typeof pt[1] !== 'number' ||
                                !isFinite(pt[0]) || !isFinite(pt[1])) return null;
                            // if (realPosition && markerInfo.isSpread) {
                            //     return null;
                            // }

                            return (<Fragment key={markerInfo.trackerId}>
                                <Marker
                                    position={pt}
                                    icon={markerInfo.icon}
                                    eventHandlers={{
                                        click: (e) => {
                                            this.viewRaceClassContestant(e, contestant.id, markerInfo);
                                        },
                                    }}
                                >
                                    {/* <Popup>
                                        <span class='font-weight-bold'>{contestant.bib}: {contestant.firstname} {contestant.lastname} {contestant.birth ? `(${util.age(contestant.birth)})` : ''}</span><br />
                                        <span class='font-weight-normal'>Siste posisjon: {util.isoDate(markerInfo.timestamp)}</span><br />
                                        <span class='font-weight-normal'>{markerInfo.country}</span><br />
                                        <span class='font-weight-normal'>{markerInfo.sportsClub}</span><br />
                                        <span class='font-weight-normal'>{markerInfo.kennel}</span><br />
                                    </Popup> */}
                                </Marker>

                                {1===2 && isAdmin && <>
                                    <Marker position={[markerInfo.point[0], markerInfo.point[1]]} icon={markerInfo.icon}>
                                        <Popup>
                                            <span class='font-weight-bold'>{markerInfo.trackerId}</span><br />
                                            <span class='font-weight-normal'>Siste posisjon: {util.isoDate(markerInfo.timestamp)}</span><br />
                                            <span class='font-weight-normal'>Distanse på sporet: {util.format(markerInfo.totalDistanceOnTrack, 1)} km</span><br />
                                            {/* <span class='font-weight-normal'>{util.format(markerInfo.speed * 3.6, 2)} m/s</span><br /> */}
                                            <span class='font-weight-normal'>Batteri: {markerInfo.battery}%</span><br />
                                        </Popup>
                                    </Marker>
                                </>}
                                {/* {markerInfo.last10Points && markerInfo.last10Points.length > 0 && <>
                                    <Polyline color={'red'} positions={markerInfo.last10Points.map(p => [p[0], p[1]])} />
                                </>} */}
                                {/* {JSON.stringify(markerInfo.last10Points, null, 4)} */}
                            </Fragment>);
                        })}

                        {yourPoint && yourPosition && <>
                            <Marker position={yourPoint} icon={iconMarkerPerson}>
                                <Popup position={yourPoint}>
                                    Your location: <pre>{JSON.stringify(yourPoint, null, 2)}</pre>
                                </Popup>
                            </Marker>
                        </>}

                        {/* Elevation hover point marker */}
                        {elevationHoverPoint && elevationHoverPoint.lat && elevationHoverPoint.lng && (() => {
                            const hoverIcon = new L.divIcon({
                                className: '',
                                iconAnchor: [12, 12],
                                iconSize: [24, 24],
                                html: `<div style="
                                    width: 24px;
                                    height: 24px;
                                    background-color: #ffc107;
                                    border: 3px solid #fff;
                                    border-radius: 50%;
                                    box-shadow: 0 2px 6px rgba(0,0,0,0.4);
                                "></div>`,
                            });
                            return (
                                <Marker
                                    position={[elevationHoverPoint.lat, elevationHoverPoint.lng]}
                                    icon={hoverIcon}
                                />
                            );
                        })()}

                        {inputMarkers.map((marker, idx) => {
                            const { lat, lng, title } = marker;
                            return (<>
                                <Marker position={[lat, lng]} icon={iconMarkerPin}>
                                    <Popup>
                                        <span class='font-weight-bold'>{title}</span><br />
                                    </Popup>
                                </Marker>
                            </>);
                        })}

                        {isAdmin && (search ? trackers.filter(tracker => {
                            const c = tracker.contestant;
                            if (!c || !c.id) return false;
                            return `${c.bib} ${c.firstname} ${c.lastname}`.toLowerCase().includes(search.toLowerCase());
                        }) : trackers).map((tracker, idx) => {
                            const contestant = tracker.contestant;
                            // Skip GPS devices without a raceClassContestant mapping
                            if (!contestant || !contestant.id) return null;
                            // In replay mode, use replayPoints (trail building), otherwise full points
                            const trailPoints = replayMode ? (tracker.replayPoints || []) : (tracker.points || []);
                            if (!trailPoints || trailPoints.length === 0) {
                                return null;
                            }

                            // Split points into segments based on GPS gaps (>15 minutes = coverage gap)
                            const segments = splitSegmentsByType(splitPointsByGaps(trailPoints, 900000));

                            return (<>
                                {segments.map((segment, segIdx) => {
                                    // Filter out invalid coordinates
                                    const validPositions = segment.points
                                        .map(p => [p[0], p[1]])
                                        .filter(pos => typeof pos[0] === 'number' && typeof pos[1] === 'number' &&
                                            isFinite(pos[0]) && isFinite(pos[1]));
                                    if (validPositions.length < 2) return null;

                                    return (<>
                                        <Polyline
                                            key={`${tracker.trackerId || idx}-seg-${segIdx}`}
                                            color={segment.isGap ? 'red' : segment.pointType === 'sat' ? '#f58a07' : (tracker.lineColor || getColor(idx))}
                                            positions={validPositions}
                                            weight={segment.isGap ? 12 : segment.pointType === 'sat' ? 3 : 2}
                                            dashArray={segment.isGap ? '10, 10' : segment.pointType === 'sat' ? null : '4, 5'}
                                            opacity={segment.isGap ? 0.25 : 1}
                                        />
                                        {!segment.isGap && segment.pointType === 'sat' && validPositions.map((pos, pIdx) => (
                                            <CircleMarker
                                                key={`${tracker.id || idx}-sat-${segIdx}-${pIdx}`}
                                                center={pos}
                                                radius={3}
                                                color='#f58a07'
                                                fillColor='#f58a07'
                                                fillOpacity={0.8}
                                                weight={1}
                                            />
                                        ))}
                                    </>);
                                })}
                            </>);
                        })}

                        {showAllSatPoints && allSatPoints.length > 0 && (search ? this.filterPointsBySearch(search, allSatPoints) : allSatPoints).map((sp, idx) => (
                            <CircleMarker
                                key={`allsat-${idx}`}
                                center={[sp.lat, sp.lng]}
                                radius={4}
                                color='#f58a07'
                                fillColor='#f58a07'
                                fillOpacity={0.7}
                                weight={1}
                            >
                                <Popup>
                                    <div style='font-size: 12px;'>
                                        <strong>SAT Point</strong><br />
                                        Tracker: {sp.trackerId}<br />
                                        Time: {sp.timestamp ? new Date(sp.timestamp).toLocaleString() : 'N/A'}<br />
                                        Battery: {sp.battery || 'N/A'}<br />
                                        Elevation: {sp.elevation ? `${Math.round(sp.elevation)}m` : 'N/A'}
                                    </div>
                                </Popup>
                            </CircleMarker>
                        ))}

                        {showDebugGpsPoints && debugGpsPoints.length > 0 && (
                            <DebugGpsClusterWrapper points={search ? this.filterPointsBySearch(search, debugGpsPoints) : debugGpsPoints} />
                        )}
                    </MapContainer>

                    {/* <xmp>{JSON.stringify(bounds, null, 4)}</xmp> */}
                    {/* {trackers.map(tracker => {
                        return (<>
                            <xmp>{JSON.stringify(tracker.points.map(p => [p[0], p[1]]))}</xmp>
                        </>);
                    })} */}
                </div>

                {/* Back button - positioned to the right of zoom controls */}
                <button
                    onClick={() => window.history.back()}
                    class='btn btn-dark rounded-circle position-absolute d-flex align-items-center justify-content-center'
                    style='top: 10px; left: 55px; z-index: 100000; width: 40px; height: 40px;'
                    title='Go back'
                >
                    <i class='fa-solid fa-arrow-left' />
                </button>

                {/* Race class selector - dropdown on mobile, buttons on desktop */}
                {(() => {
                    const isMobile = typeof window !== 'undefined' && window.innerWidth < 600;

                    if (isMobile) {
                        // Mobile: Use dropdown selector
                        const selectedValue = skipRaceClass ? 'show_all' : rcId;
                        return (
                            <div
                                class='position-absolute'
                                style='top: 10px; left: 100px; right: 55px; z-index: 100000;'
                            >
                                <div class='position-relative w-100'>
                                    <select
                                        class='form-control form-control-sm rounded-pill text-center w-100'
                                        style='height: 40px; font-weight: bold; background-color: rgba(0, 0, 0, 0.7); color: white; border: none; appearance: none; -webkit-appearance: none; padding-right: 30px;'
                                        value={selectedValue}
                                        onInput={this.setRaceClassFromSelect}
                                    >
                                        {finalRaceClasses && finalRaceClasses.map((rc) => (
                                            <option value={rc.id} style='background-color: #333;'>
                                                {rc.title}
                                            </option>
                                        ))}
                                        {showAllButton && (
                                            <option value='show_all' style='background-color: #333;'>
                                                Show all
                                            </option>
                                        )}
                                    </select>
                                    <span style='position: absolute; right: 12px; top: 50%; transform: translateY(-50%); color: white; pointer-events: none; font-size: 12px;'>▼</span>
                                </div>
                            </div>
                        );
                    }

                    // Desktop: Use button group
                    return (
                        <div
                            class='px-3 py-1 position-absolute d-flex flex-row justify-content-center'
                            style='top: 10px; left: 50%; transform: translateX(-50%); z-index: 100000; width: 85vw;'
                        >
                            {finalRaceClasses && finalRaceClasses.map((rc, idx) => {
                                return <>
                                    <button
                                        type='button'
                                        class={`btn btn-sm overflow-hidden btn-${(rcId === rc.id && !skipRaceClass) ? 'primary' : 'secondary'} rounded-${idx === 0 ? 'pill-left' : idx === finalRaceClasses.length - 1 ? 'pill-right' : 'none'}`}
                                        onClick={this.setRaceClass}
                                        data-id={rc.id}
                                    >
                                        <nobr>{rc.title}</nobr>
                                    </button>
                                </>;
                            })}
                            {showAllButton && <button
                                type='button'
                                class={`btn btn-sm btn-${skipRaceClass ? 'primary' : 'secondary'} rounded-pill ms-2`}
                                data-skip={skipRaceClass ? 0 : 1}
                                data-id={raceClass.id}
                                onClick={this.setRaceClass}
                            >
                                Show all
                            </button>}
                        </div>
                    );
                })()}
                {/* Mobile menu toggle button - only visible on small screens */}
                {(() => {
                    const isMobile = typeof window !== 'undefined' && window.innerWidth < 600;
                    const { mobileMenuOpen } = this.state;

                    return (
                        <div
                            class='position-absolute d-flex flex-column align-items-end'
                            style={`top: ${isMobile ? '10px' : '50px'}; right: ${isMobile ? '10px' : '0px'}; z-index: 100000;`}
                        >
                            {/* Mobile hamburger menu button */}
                            {isMobile && (
                                <button
                                    type='button'
                                    class={`btn btn-sm btn${mobileMenuOpen ? '-secondary' : '-dark'} rounded-circle d-flex align-items-center justify-content-center`}
                                    onClick={this.toggleMobileMenu}
                                    style='width: 40px; height: 40px;'
                                >
                                    <i class={`fa-solid fa-${mobileMenuOpen ? 'xmark' : 'bars'}`} style='font-size: 1.1em;' />
                                </button>
                            )}

                            {/* Controls - always visible on desktop, conditionally on mobile */}
                            {(!isMobile || mobileMenuOpen) && (
                                <div class={`d-flex flex-column ${isMobile ? 'mt-2 p-2 rounded' : ''}`} style={isMobile ? 'background-color: rgba(0, 0, 0, 0.7); min-width: 200px;' : ''}>
                                    {user?.id && <button
                                        type='button'
                                        class={`btn btn-sm btn-warning rounded-pill ${isMobile ? '' : 'mt-1'}`}
                                        onClick={this.toggleReplayMode}
                                        disabled={isLoadingReplay}
                                        style={isMobile ? 'min-height: 44px;' : ''}
                                    >
                                        <i class={`fa-solid fa-${replayMode ? 'stop' : isLoadingReplay ? 'spinner fa-spin' : 'backward'} me-1`} />
                                        {replayMode ? 'Exit Replay' : isLoadingReplay ? 'Loading...' : 'Replay'}
                                    </button>}
                                    {isRaceAdmin && <button
                                        type='button'
                                        class={`btn btn-sm btn-${editGeofences ? 'info' : 'outline-info'} rounded-pill ${isMobile ? '' : 'mt-1'}`}
                                        onClick={this.toggleGeofenceEdit}
                                        style={isMobile ? 'min-height: 44px;' : ''}
                                    >
                                        <i class='fa-solid fa-draw-circle me-1' />
                                        {editGeofences ? 'Exit Geofence Edit' : 'Edit Geofences'}
                                    </button>}
                                    {!editGeofences && isRaceAdmin && <button
                                        type='button'
                                        class={`btn btn-sm btn-${showGeofences ? 'success' : 'outline-secondary'} rounded-pill ${isMobile ? '' : 'mt-1'}`}
                                        onClick={this.toggleShowGeofences}
                                        style={isMobile ? 'min-height: 44px;' : ''}
                                        title='Show/hide geofence circles'
                                    >
                                        <i class={`fa-solid fa-circle-nodes me-1`} />
                                        {isMobile && (showGeofences ? 'Hide Fences' : 'Show Fences')}
                                    </button>}
                                    {(isAdmin || isRaceAdmin) && <button
                                        type='button'
                                        class={`btn btn-sm btn-${geofenceSoundEnabled ? 'success' : 'outline-secondary'} rounded-pill ${isMobile ? '' : 'mt-1'}`}
                                        onClick={() => {
                                            if (!geofenceSoundEnabled) {
                                                geofenceArrivalSound.play().catch(() => {});
                                            }
                                            this.setState({ geofenceSoundEnabled: !geofenceSoundEnabled });
                                        }}
                                        style={isMobile ? 'min-height: 44px;' : ''}
                                        title='Toggle geofence arrival/departure sounds'
                                    >
                                        <i class={`fa-solid fa-${geofenceSoundEnabled ? 'volume-high' : 'volume-xmark'}`} />
                                        {isMobile && <span class='ms-2'>{geofenceSoundEnabled ? 'Sound On' : 'Sound Off'}</span>}
                                    </button>}
                                    {isAdmin && <button
                                        type='button'
                                        class={`btn btn-sm btn-${showAllSatPoints ? 'warning' : 'outline-secondary'} rounded-pill ${isMobile ? '' : 'mt-1'}`}
                                        onClick={this.toggleShowAllSatPoints}
                                        style={isMobile ? 'min-height: 44px;' : ''}
                                        title='Show all satellite GPS points on map'
                                        disabled={loadingAllSatPoints}
                                    >
                                        <i class={`fa-solid fa-satellite-dish me-1`} />
                                        {loadingAllSatPoints ? 'Loading...' : ''}
                                        {!loadingAllSatPoints && isMobile && (showAllSatPoints ? 'Hide SAT Points' : 'SAT Points')}
                                        {!loadingAllSatPoints && !isMobile && (showAllSatPoints && allSatPoints.length > 0 ? `SAT (${allSatPoints.length})` : '')}
                                    </button>}
                                    {isAdmin && <button
                                        type='button'
                                        class={`btn btn-sm btn-${showDebugGpsPoints ? 'info' : 'outline-secondary'} rounded-pill ${isMobile ? '' : 'mt-1'}`}
                                        onClick={this.toggleShowDebugGpsPoints}
                                        style={isMobile ? 'min-height: 44px;' : ''}
                                        title='Debug: Show all GPS points (GSM + SAT) with clustering'
                                        disabled={loadingDebugGpsPoints}
                                    >
                                        <i class={`fa-solid fa-map-location-dot me-1`} />
                                        {loadingDebugGpsPoints ? 'Loading...' : ''}
                                        {!loadingDebugGpsPoints && isMobile && (showDebugGpsPoints ? 'Hide Debug GPS' : 'Debug GPS')}
                                        {!loadingDebugGpsPoints && !isMobile && (showDebugGpsPoints && debugGpsPoints.length > 0 ? `GPS (${debugGpsPoints.length})` : '')}
                                    </button>}
                                    {showDebugGpsPoints && <div
                                        class={`d-flex flex-row align-items-center gap-2 ${isMobile ? '' : 'mt-1'}`}
                                        style='font-size: 10px;'
                                    >
                                        <span><span style='display:inline-block;width:10px;height:10px;border-radius:50%;background:#2196F3;'></span> GSM</span>
                                        <span><span style='display:inline-block;width:10px;height:10px;border-radius:50%;background:#f58a07;'></span> SAT</span>
                                        <span><span style='display:inline-block;width:10px;height:10px;border-radius:50%;background:#9C27B0;'></span> Mixed</span>
                                    </div>}
                                    {replayMode && (isAdmin || isRaceAdmin) && <button
                                        type='button'
                                        class={`btn btn-sm btn-${showRealPointsOnly ? 'success' : 'outline-secondary'} rounded-pill ${isMobile ? '' : 'mt-1'}`}
                                        onClick={this.toggleShowRealPointsOnly}
                                        style={isMobile ? 'min-height: 44px;' : ''}
                                        title='Show only real GPS points (disable interpolation)'
                                    >
                                        <i class={`fa-solid fa-location-dot me-1`} />
                                        {isMobile && (showRealPointsOnly ? 'Real Points' : 'Interpolated')}
                                    </button>}
                                    <button
                                        type='button'
                                        class={`btn btn-sm btn-${showCoverage ? 'success' : 'outline-secondary'} rounded-pill ${isMobile ? '' : 'mt-1'}`}
                                        onClick={this.toggleShowCoverage}
                                        style={isMobile ? 'min-height: 44px;' : ''}
                                    >
                                        <i class='fa-solid fa-signal me-1' />
                                        <Text id='race.coverage'>Coverage</Text>
                                    </button>
                                    {showCoverage && <div
                                        class={`bg-body text-body rounded px-2 py-1 d-flex flex-column ${isMobile ? '' : 'mt-1'}`}
                                        style='font-size: 11px; max-width: 220px;'
                                    >
                                        <span>
                                            <span style='display:inline-block;width:10px;height:10px;background:#dc3545;opacity:0.6;' />
                                            {' '}<Text id='race.coverage-bad'>Poor mobile coverage</Text>
                                        </span>
                                        <span>
                                            <span style='display:inline-block;width:10px;height:10px;background:#198754;opacity:0.6;' />
                                            {' '}<Text id='race.coverage-good'>Good mobile coverage</Text>
                                        </span>
                                        <span class='text-body-secondary'>
                                            <Text id='race.coverage-unknown'>No colour: too little data</Text>
                                        </span>
                                    </div>}
                                    {noReplayData && <div
                                        class='alert alert-warning py-1 px-2 mt-1 mb-0 rounded-pill'
                                        style='font-size: 0.8em;'
                                    >
                                        <i class='fa-solid fa-triangle-exclamation me-1' />
                                        No replay data available
                                    </div>}
                                    <div class={`d-flex ${isMobile ? 'flex-column' : 'flex-row'} mt-1`}>
                                        <button
                                            type='button'
                                            class={`btn btn-sm btn${yourPosition ? '-success' : '-outline-secondary'} rounded-pill`}
                                            onClick={this.toggleCurrentPosition}
                                            style={isMobile ? 'min-height: 44px;' : ''}
                                        >
                                            <i class={`fa-solid fa-location-crosshairs`} />
                                            {isMobile && <span class='ms-2'>Your location</span>}
                                        </button>
                                        <button
                                            type='button'
                                            class={`btn btn-sm btn-outline-secondary rounded-pill ${isMobile ? 'mt-1' : 'ms-1'}`}
                                            onClick={this.zoomToAllMushers}
                                            style={isMobile ? 'min-height: 44px;' : ''}
                                            title='Zoom to all mushers'
                                        >
                                            <i class='fa-solid fa-users' />
                                            {isMobile && <span class='ms-2'>All mushers</span>}
                                        </button>

                                        <div class={`d-flex flex-column ${isMobile ? 'mt-1' : 'ms-2'}`} style={isMobile ? '' : 'max-width: 50vw;'}>
                                            <div class='input-group mt-1'>
                                                <span
                                                    class='input-group-text rounded-pill-left text-white'
                                                    style={`background-color: rgba(0, 0, 0, 0.6);`}
                                                >
                                                    <i class='fa-regular fa-magnifying-glass'></i>
                                                </span>
                                                <input
                                                    class={`form-control form-control-sm text-white ${!search ? 'rounded-pill-right' : ''}`}
                                                    style={`background-color: rgba(0, 0, 0, 0.6); ${isMobile ? 'min-height: 44px;' : 'width: 50px;'}`}
                                                    type='text'
                                                    value={search}
                                                    onInput={this.searchInput}
                                                    onKeyDown={this.handleKeyDown}
                                                    placeholder='Search...'
                                                />
                                                {search && <>
                                                    <button class='btn btn-danger btn-sm rounded-pill-right' type='button' onClick={this.clearSearch}>
                                                        <i class='fa-solid fa-circle-xmark'></i>
                                                    </button>
                                                </>}
                                            </div>
                                            {mapTypeFixed ? <></> : <>
                                                <div class='input-group mt-1'>
                                                    <span
                                                        class='input-group-text rounded-pill-left text-white'
                                                        style={`background-color: rgba(0, 0, 0, 0.6);`}
                                                    >
                                                        <i class='fa-regular fa-map'></i>
                                                    </span>
                                                        <select
                                                            class={`form-control form-control-sm ${!search ? 'rounded-pill-right' : ''}`}
                                                            onInput={this.changeMapType}
                                                            value={mapType}
                                                            style={isMobile ? 'min-height: 44px;' : ''}
                                                        >
                                                            <option value='topo'>Kartverket - Topo</option>
                                                            <option value='topograatone'>Kartverket - Topo grå</option>
                                                            <option value='openstreetmap'>Open Streetmap</option>
                                                        </select>
                                                </div>
                                            </>}
                                            <button
                                                type='button'
                                                class={`btn btn-sm btn-success rounded-pill mt-1`}
                                                onClick={this.showRaceResults}
                                                style={isMobile ? 'min-height: 44px;' : ''}
                                            >
                                                <i class='fa-solid fa-list' /> <Text id='race.results'>Results</Text>
                                            </button>
                                            <button
                                                type='button'
                                                class={`btn btn-sm btn${showElevationProfile ? '-info' : '-outline-secondary'} rounded-pill mt-1`}
                                                onClick={this.toggleElevationProfile}
                                                title='Toggle elevation profile'
                                                style={isMobile ? 'min-height: 44px;' : ''}
                                            >
                                                <i class='fa-solid fa-mountain' />
                                                {isMobile && <span class='ms-2'>Elevation</span>}
                                            </button>
                                            {!replayMode && (
                                                <button
                                                    type='button'
                                                    class={`btn btn-sm btn${autoUpdate ? '-primary' : '-outline-secondary'} rounded-pill mt-1`}
                                                    onClick={this.toggleAutoUpdate}
                                                    title='Toggle auto-update (refreshes every 60 seconds)'
                                                    style={isMobile ? 'min-height: 44px;' : ''}
                                                >
                                                    <i class={`fa-solid fa-${autoUpdate ? 'rotate fa-spin' : 'pause'}`} style={autoUpdate ? 'animation-duration: 3s;' : ''} />
                                                    {autoUpdate && <span class='ms-1' style='font-size: 0.85em;'>{autoUpdateCountdown}s</span>}
                                                    {isMobile && <span class='ms-2'>Auto-update</span>}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {((race && race.sponsors?.length > 0) || (raceClass && raceClass.sponsors?.length > 0)) && (!isMobile || mobileMenuOpen) && <>
                                <div class='w-100 d-flex flex-column justify-content-between mt-3'>
                                    {race && race.sponsors?.length > 0 && race.sponsors.map(sponsor => {
                                        return (<>
                                            <div class='mx-3 d-flex flex-row justify-content-end'>
                                                <img
                                                    src={localUtil.displayImg(sponsor, this.props, '220x')}
                                                    class='img-fluid mx-1'
                                                    style='max-height: 40px;'
                                                />
                                            </div>
                                        </>);
                                    })}
                                    {raceClass && raceClass.sponsors?.length > 0 && raceClass.sponsors.map(sponsor => {
                                        return (<>
                                            <div class='mx-3 d-flex flex-row justify-content-end'>
                                                <img
                                                    src={localUtil.displayImg(sponsor, this.props, '220x')}
                                                    style='max-height: 40px;'
                                                />
                                            </div>
                                        </>);
                                    })}
                                </div>
                            </>}
                        </div>
                    );
                })()}

                {/* {isAdmin && isFixed && <div
                    class={`w-90 rounded-lg px-3 py-1 position-absolute ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} overflow-auto`}
                    style='
                        top: 82vh;
                        left: 50%;
                        transform: translateX(-50%);
                        z-index: 2;
                        height: 12vh !important;
                    '
                >
                    <div class='table-responsive' style='font-size: 0.8em;'>
                        <table class='table table-striped table-hover table-sm'>
                            <thead>
                                <tr>
                                    <th class='p-0'>first timestamp</th>
                                    <th class='p-0'>timestamp</th>
                                    <th class='p-0'>trackerId</th>
                                    <th class='p-0'>bib: name</th>
                                    <th class='p-0'>elevation</th>
                                    <th class='p-0'>speed</th>
                                    <th class='p-0 text-center'>battery</th>
                                    <th class='p-0 text-center'>points</th>
                                </tr>
                            </thead>
                            <tbody>
                                {trackers.sort(fieldSorter(['-count'])).map(pt => {
                                    const rcc = raceClassContestants.find(rcc => rcc.id === pt.raceClassContestant);
                                    return (<>
                                        <tr
                                            class='box-line'
                                            onClick={e => this.viewRaceClassContestant(e, rcc.id)}
                                            // data-id={id}
                                        >
                                            <td class='p-0'>{util.isoDate(pt.firstTimestamp)}</td>
                                            <td class='p-0'>{util.isoDate(pt.timestamp)}</td>
                                            <td class='p-0'>{pt.trackerId}</td>
                                            <td class='p-0'>{rcc?.bib}: {rcc?.firstname} {rcc?.lastname}</td>
                                            <td class='p-0'>{pt.elevation} moh</td>
                                            <td class='p-0'>{util.format(pt.speed * 3.6, 1)} km/t</td>
                                            <td class='p-0 text-end'>{pt.battery}%</td>
                                            <td class='p-0 text-end'>{pt.count}</td>
                                        </tr>
                                    </>);
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>} */}

                {/* Replay Controls */}
                {replayMode && (
                    <ReplayControls
                        isPlaying={replayPlaying}
                        currentTime={replayCurrentTime}
                        startTime={replayStartTime}
                        endTime={replayEndTime}
                        speed={replaySpeed}
                        onPlayPause={this.handleReplayPlayPause}
                        onSeek={this.handleReplaySeek}
                        onSpeedChange={this.handleReplaySpeedChange}
                        onClose={this.toggleReplayMode}
                        stores={this.props.stores}
                        isFixed={isFixed}
                        bottomOffset={showElevationProfile && elevationProfile?.length > 0 ? (() => {
                            const w = typeof window !== 'undefined' ? window.innerWidth : 1024;
                            if (w < 600) return 160;      // Small mobile: 130px graph + header
                            if (w < 900) return 180;      // Mobile: 150px graph + header
                            if (w < 1200) return 210;     // Tablet: 180px graph + header
                            if (w < 1600) return 230;     // Medium: 200px graph + header
                            return 250;                    // Large: 220px graph + header
                        })() : 0}
                    />
                )}

                {/* Elevation Profile */}
                {showElevationProfile && (
                    <div
                        class={`${isFixed ? 'position-fixed' : 'position-absolute'} w-100 d-flex justify-content-center`}
                        style='bottom: 0; left: 0; z-index: 1000;'
                    >
                    <div style='width: 100%; max-width: 960px;'>
                        {elevationProfile && elevationProfile.length > 0 ? (
                            <ElevationProfile
                                profile={elevationProfile}
                                lineColor='#ffc107'
                                stores={this.props.stores}
                                onPointClick={this.handleElevationPointClick}
                                onHover={this.handleElevationHover}
                                trackers={trackers}
                                totalTrackLength={totalTrackLength}
                            />
                        ) : (
                            <div
                                class={`d-flex align-items-center justify-content-between px-3 py-2 ${darkmode ? 'bg-dark text-light' : 'bg-light text-dark'}`}
                                style='border-top: 1px solid #ddd;'
                            >
                                <span class='small'>
                                    <i class='fa-solid fa-mountain me-2' />
                                    Elevation Profile - No elevation data available
                                </span>
                                <button
                                    type='button'
                                    class='btn btn-sm btn-outline-secondary'
                                    onClick={this.toggleElevationProfile}
                                >
                                    <i class='fa-solid fa-xmark' />
                                </button>
                            </div>
                        )}
                    </div>
                    </div>
                )}

                {/* GPS Tracker Warnings for Race Admins */}
                {(isRaceAdmin || isAdmin) && trackers?.length > 0 && (() => {
                    const lowBatteryTrackers = trackers.filter(t => t.battery <= 25 && t.contestant);

                    // Stale trackers: no data in last 30 minutes
                    const now = new Date().getTime();
                    const thirtyMinutesAgo = now - (30 * 60 * 1000);
                    const staleTrackers = trackers.filter(t => {
                        if (!t.contestant || !t.timestamp) return false;
                        const lastUpdate = new Date(t.timestamp).getTime();
                        return lastUpdate < thirtyMinutesAgo;
                    });

                    const totalWarnings = lowBatteryTrackers.length + staleTrackers.length;
                    if (totalWarnings === 0) return null;

                    return (
                        <div
                            class={`position-absolute rounded shadow ${darkmode ? 'bg-dark text-light' : 'bg-white text-dark'}`}
                            style='top: 110px; left: 5px; z-index: 999;'
                        >
                            <button
                                type='button'
                                class={`btn btn-sm d-flex align-items-center ${darkmode ? 'btn-dark' : 'btn-light'} border-0`}
                                onClick={() => this.setState({ showTrackerWarnings: !this.state.showTrackerWarnings })}
                            >
                                {lowBatteryTrackers.length > 0 && <span class='badge bg-warning text-dark me-1'><i class='fa-solid fa-battery-quarter' /> {lowBatteryTrackers.length}</span>}
                                {staleTrackers.length > 0 && <span class='badge bg-danger'><i class='fa-solid fa-signal-slash' /> {staleTrackers.length}</span>}
                                <i class={`fa-solid fa-chevron-${this.state.showTrackerWarnings ? 'up' : 'down'} ms-2 small`} />
                            </button>

                            {this.state.showTrackerWarnings && (
                                <div class='p-2' style='max-height: 250px; overflow-y: auto;'>
                                    {lowBatteryTrackers.length > 0 && <>
                                        <div class='d-flex align-items-center mb-2'>
                                            <i class='fa-solid fa-battery-quarter text-warning me-2' />
                                            <strong class='small'>Low Battery</strong>
                                        </div>
                                        {lowBatteryTrackers.map(tracker => (
                                            <div class='d-flex justify-content-between small border-bottom py-1' style='white-space: nowrap;'>
                                                <span class='me-3'>#{tracker.contestant.bib} {tracker.contestant.firstname} {tracker.contestant.lastname}</span>
                                                <span class={`text-nowrap ${tracker.battery === 0 ? 'text-danger fw-bold' : 'text-warning'}`}>{tracker.battery}%</span>
                                            </div>
                                        ))}
                                    </>}

                                    {staleTrackers.length > 0 && <>
                                        <div class={`d-flex align-items-center mb-2 ${lowBatteryTrackers.length > 0 ? 'mt-3' : ''}`}>
                                            <i class='fa-solid fa-signal-slash text-danger me-2' />
                                            <strong class='small'>No Signal</strong>
                                        </div>
                                        {staleTrackers.map(tracker => {
                                            const lastUpdate = new Date(tracker.timestamp);
                                            const totalMinutes = Math.round((now - lastUpdate.getTime()) / (60 * 1000));
                                            const days = Math.floor(totalMinutes / (60 * 24));
                                            const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
                                            const minutes = totalMinutes % 60;
                                            let timeAgo = '';
                                            if (days > 0) timeAgo += `${days}d `;
                                            if (hours > 0 || days > 0) timeAgo += `${hours}h `;
                                            timeAgo += `${minutes}m`;
                                            return (
                                                <div class='d-flex justify-content-between small border-bottom py-1' style='white-space: nowrap;'>
                                                    <span class='me-3'>#{tracker.contestant.bib} {tracker.contestant.firstname} {tracker.contestant.lastname}</span>
                                                    <span class='text-danger text-nowrap'>{timeAgo}</span>
                                                </div>
                                            );
                                        })}
                                    </>}
                                </div>
                            )}
                        </div>
                    );
                })()}

                <NewsTickerOverlay
                    stores={this.props.stores}
                    raceId={race.id}
                    autoStart
                    bottom='20px'
                    maxWidth='calc(100% - 160px)'
                />
                <StreamingTrackingOverlay
                    stores={this.props.stores}
                    raceId={race.id}
                    bottom='20px'
                />
            </div>
        </>);
    }
}

export default RaceClassContestantList;
